import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiArrowLeft,
  FiCheckCircle,
  FiXCircle,
  FiTruck,
  FiPackage,
  FiSearch,
  FiClock,
  FiMessageCircle,
  FiMapPin,
  FiDollarSign,
  FiAlertTriangle,
  FiCreditCard,
  FiShield,
  FiHome,
  FiSend,
  FiPaperclip,
  FiCamera,
  FiImage,
  FiFileText,
  FiMic,
  FiSquare,
  FiSmile,
  FiRotateCcw,
  FiX,
} from 'react-icons/fi';
import API, { getImagenUrl, getSchemaName } from 'shared/api';
import { fmtMonto } from 'shared/format';
import { useAuth } from 'shared/context/AuthContext';
import { Modal } from 'components/basicos';
import MapaUbicacion from 'shared/components/MapaUbicacion';
import MessageList from './MessageList';
import ProductoItem, { kgToDisplay, displayToGramos } from './ProductoItem';
import ProductoBuscadorModal from './ProductoBuscadorModal';
import ComboBuscadorModal from './ComboBuscadorModal';
import ComboEdicionModal from './ComboEdicionModal';
import BotonFotoRemota from 'shared/components/BotonFotoRemota';

function isDark() {
  try {
    return document.documentElement.getAttribute('data-dark') === 'true';
  } catch {
    return false;
  }
}

const DEBOUNCE_MS = 500;

const pdCampo = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--agx-border)',
  borderRadius: '8px',
  fontSize: '13px',
  outline: 'none',
  boxSizing: 'border-box',
  background: 'var(--agx-input-bg)',
  color: 'var(--agx-text)',
  fontFamily: 'inherit',
};

const VALID_ESTADOS = new Set([
  'pendiente', 'en_revision', 'aprobado', 'esperando_pago',
  'pago_en_verificacion', 'pagado', 'armado', 'armado_cocina',
  'listo', 'en_camino', 'llegada', 'entregado',
  'rechazado', 'cancelado', 'despachado',
]);

const formatearFechaCompleta = (f) => {
  if (!f) return '';
  const d = new Date(f);
  return (
    d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' }) +
    ' ' +
    d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
  );
};

const PedidoDetalle = ({
  pedido,
  onBack,
  readOnly = false,
  onIniciarChat,
  embedded = false,
  chatMensajes = [],
  chatCargandoMas = false,
  chatHasMore = false,
  onChatCargarMas,
  onChatEnviar,
  onChatRefresh,
  onChatAbrir,
  onChatToggle,
  onTomarChat,
  onLiberarChat,
  isMobile = false,
}) => {
  const [estado, setEstado] = useState(pedido.estado || 'pendiente');
  const [actualizando, setActualizando] = useState(false);
  const [productos, setProductos] = useState([]);
  const [productosOriginales, setProductosOriginales] = useState([]);
  const [mostrarBuscador, setMostrarBuscador] = useState(false);
  const [mostrarCombos, setMostrarCombos] = useState(false);
  const [comboEdicion, setComboEdicion] = useState(null); // { index, producto }
  const [confirmarPedido, setConfirmarPedido] = useState(false);
  const [confirmacionEnviada, setConfirmacionEnviada] = useState(
    pedido.confirmacion_pendiente || false
  );
  const [condicionVenta, setCondicionVenta] = useState(pedido.condicion_venta || 'contado');
  const [tipoEntrega, setTipoEntrega] = useState(pedido.tipo_entrega || '');
  const [costoEnvio, setCostoEnvio] = useState(pedido.costo_envio || 0);
  const costoEnvioRef = useRef(costoEnvio);
  useEffect(() => { costoEnvioRef.current = costoEnvio; }, [costoEnvio]);

  const [tieneModificaciones, setTieneModificaciones] = useState(false);
  const [pesosLocales, setPesosLocales] = useState({});
  const [imagenExpandida, setImagenExpandida] = useState(false);
  const [confirmarVerificacion, setConfirmarVerificacion] = useState(null);
  const [pagos, setPagos] = useState([]);
  const [cargandoPagos, setCargandoPagos] = useState(false);
  const [pagoNotas, setPagoNotas] = useState({});
  const [pagoVerificandoId, setPagoVerificandoId] = useState(null);
  const [mostrarCargarPago, setMostrarCargarPago] = useState(false);
  const [metodosPago, setMetodosPago] = useState([]);
  const [monedas, setMonedas] = useState([]);
  const [tasaBsFallback, setTasaBsFallback] = useState(null);
  const [cargandoPago, setCargandoPago] = useState(false);
  const [pagosBorrador, setPagosBorrador] = useState([]);
  const [pagoLinea, setPagoLinea] = useState({ metodo_pago_id: '', monto: '', referencia: '', archivo: null, previewUrl: '' });
  const [avisoError, setAvisoError] = useState(null);
  const [confirmarCancelar, setConfirmarCancelar] = useState(false);
  const [motivoCancelacion, setMotivoCancelacion] = useState('');
  const [confirmarDevolver, setConfirmarDevolver] = useState(false);
  const [confirmarDevolverFinal, setConfirmarDevolverFinal] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [eliminandoIdx, setEliminandoIdx] = useState(null);
  const [autoSaveError, setAutoSaveError] = useState(null);
  const [autoSaving, setAutoSaving] = useState(false);
  const [erpSync, setErpSync] = useState({
    estado: pedido.erp_sync_estado || 'none',
    numfac: pedido.erp_numfac || null,
    error: pedido.erp_error || null,
  });
  const [migrando, setMigrando] = useState(false);
  const [avisoErp, setAvisoErp] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [mobileTab, setMobileTab] = useState('pedido');
  const [chatTexto, setChatTexto] = useState('');
  const [chatEnviando, setChatEnviando] = useState(false);
  const [chatImagenesPreview, setChatImagenesPreview] = useState([]);
  const [chatImagenModalOpen, setChatImagenModalOpen] = useState(false);
  const [chatImagenSeleccionadaIdx, setChatImagenSeleccionadaIdx] = useState(0);
  const [chatDocPreview, setChatDocPreview] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [grabando, setGrabando] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const speechRef = useRef(null);
  const transcripcionRef = useRef('');
  const [confirmarEntregadoEncomienda, setConfirmarEntregadoEncomienda] = useState(false);
  const [confirmarSalioLocal, setConfirmarSalioLocal] = useState(false);
  const [fechaEntregaEncomienda, setFechaEntregaEncomienda] = useState(
    new Date().toISOString().split('T')[0]
  );
  const autoSaveTimerRef = useRef(null);
  const costoEnvioDirtyRef = useRef(false);
  const costoEnvioTimerRef = useRef(null);
  const pedidoRef = useRef(pedido);
  const ultimosProductosRef = useRef([]);
  const productosRef = useRef([]);
  const prevPedidoSnapshotRef = useRef('');
  const chatInputRef = useRef(null);
  const chatFileInputRef = useRef(null);
  const chatCamaraInputRef = useRef(null);
  const chatDocInputRef = useRef(null);
  const chatMessagesRef = useRef(null);

  const { tieneVisibilidad } = useAuth();
  const { usuario } = useAuth();
  const { tienePermiso } = useAuth();
  const v = (c) => tieneVisibilidad(c);

  // El write-back a ADVIN (factura en espera) solo aplica a Full Chery.
  const esFullChery = getSchemaName() === 'full_chery';

  const EMOJIS = [
    '😀', '😁', '😂', '🤣', '😊', '😍', '😘', '😎',
    '🤔', '😅', '😢', '😭', '😡', '👍', '👎', '🙏',
    '👏', '💪', '🙌', '❤️', '🔥', '✅', '⚠️', '🎉',
  ];

  useEffect(() => {
    pedidoRef.current = pedido;
  }, [pedido]);

  useEffect(() => {
    let activo = true;
    API.get(`/pedidos/${pedido.id}/historial`)
      .then((r) => {
        if (activo && r.data.status === 'success') setHistorial(r.data.data || []);
      })
      .catch(() => {});
    return () => {
      activo = false;
    };
  }, [pedido.id, estado]);

  // Mientras la factura este en espera de confirmacion de sync, se consulta el estado
  // periodicamente para reflejar "procesado" / "error" sin recargar el detalle.
  useEffect(() => {
    if (erpSync.estado !== 'pendiente') return undefined;
    const t = setInterval(async () => {
      try {
        const r = await API.get(`/pedidos/${pedido.id}/erp-sync`);
        if (r.data.status === 'success') {
          const d = r.data.data || {};
          setErpSync({
            estado: d.erp_sync_estado || 'none',
            numfac: d.erp_numfac || null,
            error: d.erp_error || null,
          });
        }
      } catch {
        // Silencioso: se reintenta en el siguiente ciclo.
      }
    }, 15000);
    return () => clearInterval(t);
  }, [erpSync.estado, pedido.id]);

  useEffect(() => {
    const snapshot = `${pedido.id}|${pedido.estado}|${typeof pedido.productos === 'string' ? pedido.productos : JSON.stringify(pedido.productos)}|${pedido.total}|${pedido.costo_envio || 0}`;
    if (snapshot === prevPedidoSnapshotRef.current) {
      return;
    }
    prevPedidoSnapshotRef.current = snapshot;
    if ((pedido.costo_envio || 0) !== costoEnvio) { setCostoEnvio(pedido.costo_envio || 0); costoEnvioDirtyRef.current = false; }
    if (pedido.condicion_venta) setCondicionVenta(pedido.condicion_venta);
    const prods = (() => {
      if (!pedido.productos) return [];
      if (Array.isArray(pedido.productos)) return pedido.productos;
      if (typeof pedido.productos === 'object') return [];
      try {
        return JSON.parse(pedido.productos);
      } catch {
        return [];
      }
    })();

    const storageKey = `pedido_orig_${pedido.id}`;
    sessionStorage.removeItem(storageKey);

    const copia = JSON.parse(JSON.stringify(prods));
    setProductos(prods);
    setProductosOriginales(copia);
    ultimosProductosRef.current = JSON.parse(JSON.stringify(prods));

    const pesos = {};
    prods.forEach((p, i) => {
      if (p.pesable) pesos[i] = kgToDisplay(p.cantidad || 1000);
    });
    setPesosLocales(pesos);
    const nuevoEstado = pedido.estado;
    if (nuevoEstado && nuevoEstado !== 'productos_actualizados' && VALID_ESTADOS.has(nuevoEstado)) {
      setEstado(nuevoEstado);
    }
    setConfirmacionEnviada(pedido.confirmacion_pendiente || false);
    setTieneModificaciones(false);

    const sede = pedido.codigo_sede;
    const codigos = [...new Set(prods.map((p) => p.codigo).filter(Boolean))];
    if (sede && codigos.length > 0) {
      API.get(
        `/pedidos/productos/buscar?q=${encodeURIComponent(codigos.join(' '))}&sede=${sede}&limite=${codigos.length + 5}`
      )
        .then((r) => {
          if (r.data.status === 'success') {
            const map = {};
            (r.data.data || []).forEach((item) => {
              map[item.codigo] = item.stock ?? item.n_cantidad ?? 0;
            });
            setProductos((prev) =>
              prev.map((p) =>
                p.codigo && map[p.codigo] !== undefined ? { ...p, stock: map[p.codigo] } : p
              )
            );
            setProductosOriginales((prev) =>
              prev.map((p) =>
                p.codigo && map[p.codigo] !== undefined ? { ...p, stock: map[p.codigo] } : p
              )
            );
            ultimosProductosRef.current = ultimosProductosRef.current.map((p) =>
              p.codigo && map[p.codigo] !== undefined ? { ...p, stock: map[p.codigo] } : p
            );
          }
        })
        .catch(() => {});
    }
  }, [pedido, pedido.id, pedido.productos, costoEnvio]);

  useEffect(() => {
    setTieneModificaciones(JSON.stringify(productos) !== JSON.stringify(productosOriginales));
  }, [productos, productosOriginales]);

  const total = productos.reduce((s, p) => {
    const base = p.pesable
      ? ((p.precio || 0) * (p.cantidad || 1)) / 1000
      : (p.precio || 0) * (p.cantidad || 1);
    const etiquetas = p.etiquetas || [];
    const etiqExtra = p.etiquetas_extra || etiquetas.reduce((t, e) => t + (e.precio || 0), 0);
    return s + base + etiqExtra;
  }, 0);

  const doAutoSave = useCallback(
    async (prods) => {
      if (readOnly || estado !== 'en_revision') return;
      setAutoSaving(true);
      setAutoSaveError(null);
      const anteriores = ultimosProductosRef.current;
      let mensaje = '';
      if (anteriores.length === 0) {
        mensaje = 'El asesor modifico los productos del pedido.';
      } else {
        const cambios = [];
        prods.forEach((p) => {
          const ant = anteriores.find((a) => a.codigo === p.codigo);
          const n = p.nombre || p.codigo || '';
          if (!ant) cambios.push(`+ Agrego: ${n}`);
          else if (ant.cantidad !== p.cantidad) {
            const d = p.pesable
              ? `${kgToDisplay(ant.cantidad)}kg → ${kgToDisplay(p.cantidad)}kg`
              : `${ant.cantidad} → ${p.cantidad}`;
            cambios.push(`~ ${n}: ${d}`);
          }
        });
        anteriores.forEach((ant) => {
          if (!prods.find((p) => p.codigo === ant.codigo))
            cambios.push(`- Elimino: ${ant.nombre || ant.codigo}`);
        });
        mensaje =
          cambios.length > 0
            ? 'El asesor realizo cambios:\n' + cambios.join('\n')
            : 'El asesor actualizo los productos.';
      }
      try {
        const productosClean = prods.map(({ stock, n_cantidad, ...rest }) => rest);
        const res = await API.put(`/pedidos/${pedidoRef.current.id}/notificar-cambio`, {
          schema_name: getSchemaName(),
          productos: productosClean,
          idcliente: pedidoRef.current.idcliente,
          mensaje,
          costo_envio: costoEnvioRef.current,
        });
        if (res.data.status === 'success') {
          ultimosProductosRef.current = JSON.parse(JSON.stringify(prods));
          costoEnvioDirtyRef.current = false;
        } else {
          setAutoSaveError(res.data.message || 'Error al guardar cambios');
        }
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.response?.data?.message || 'Error al guardar cambios. Verifica la conexion.';
        setAutoSaveError(msg);
        console.error('Error auto-guardando:', e);
      }
      setAutoSaving(false);
    },
    [readOnly, estado]
  );

  const programarAutoSave = useCallback(
    (prods) => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = setTimeout(() => doAutoSave(prods), DEBOUNCE_MS);
    },
    [doAutoSave]
  );

  useEffect(() => {
    productosRef.current = productos;
  }, [productos]);

  useEffect(() => {
    return () => {
      if (costoEnvioTimerRef.current) {
        clearTimeout(costoEnvioTimerRef.current);
      }
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      const prods = productosRef.current;
      if (prods && prods.length > 0 && (autoSaveTimerRef.current || costoEnvioDirtyRef.current)) {
        doAutoSave(prods);
      }
    };
  }, [doAutoSave]);

  useEffect(() => {
    if (!costoEnvioDirtyRef.current) return;
    if (estado !== 'en_revision' || readOnly) return;
    if (costoEnvioTimerRef.current) clearTimeout(costoEnvioTimerRef.current);
    costoEnvioTimerRef.current = setTimeout(() => {
      if (productosRef.current.length > 0) {
        doAutoSave(productosRef.current);
      }
    }, DEBOUNCE_MS);
    return () => {
      if (costoEnvioTimerRef.current) clearTimeout(costoEnvioTimerRef.current);
    };
  }, [costoEnvio, estado, readOnly, doAutoSave]);

  const actualizarEstado = async (nuevoEstado) => {
    if (readOnly) return;
    setActualizando(true);
    try {
      const res = await API.put(`/pedidos/actualizar-estado/${pedido.id}`, {
        schema_name: getSchemaName(),
        estado: nuevoEstado,
      });
      if (res.data.status === 'success') setEstado(nuevoEstado);
    } catch (e) {
      console.error('Error:', e);
    }
    setActualizando(false);
  };

  const migrarAdvin = async () => {
    if (readOnly) return;
    setMigrando(true);
    setAvisoErp(null);
    try {
      const res = await API.post(`/pedidos/${pedido.id}/migrar-advin`);
      if (res.data.status === 'success') {
        setErpSync({
          estado: res.data.data?.erp_sync_estado || 'pendiente',
          numfac: res.data.data?.erp_numfac || null,
          error: null,
        });
      } else {
        setAvisoErp(res.data.message || 'Error al migrar a ADVIN');
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.response?.data?.message;
      setAvisoErp(detail || 'Error al migrar a ADVIN');
    }
    setMigrando(false);
  };

  const cambiarTipoEntrega = async (nuevoTipo) => {
    if (readOnly || !nuevoTipo) return;
    try {
      const res = await API.put(`/pedidos/${pedido.id}/tipo-entrega`, {
        schema_name: getSchemaName(),
        tipo_entrega: nuevoTipo,
      });
      if (res.data.status === 'success') {
        setTipoEntrega(nuevoTipo);
        pedidoRef.current = { ...pedidoRef.current, tipo_entrega: nuevoTipo };
      } else {
        alert(res.data.message || 'Error al cambiar el tipo de entrega');
      }
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error al cambiar el tipo de entrega');
    }
  };

  const confirmarRevision = async () => {
    setActualizando(true);
    try {
      const resumen = tieneModificaciones
        ? [
            ...productos
              .filter((p) => !productosOriginales.some((o) => o.codigo === p.codigo))
              .map(
                (p) =>
                  `+ Agregado: ${p.nombre || p.descripcion || ''} x${p.pesable ? kgToDisplay(p.cantidad) + 'kg' : p.cantidad}`
              ),
            ...productos
              .filter((p) => {
                const orig = productosOriginales.find((o) => o.codigo === p.codigo);
                return orig && orig.cantidad !== p.cantidad;
              })
              .map((p) => {
                const orig = productosOriginales.find((o) => o.codigo === p.codigo);
                const n = p.nombre || p.descripcion || '';
                return p.pesable
                  ? `~ ${n}: ${kgToDisplay(orig.cantidad)} → ${kgToDisplay(p.cantidad)} kg`
                  : `~ ${n}: ${orig.cantidad} → ${p.cantidad} und`;
              }),
            ...productosOriginales
              .filter((o) => !productos.some((p) => p.codigo === o.codigo))
              .map(
                (o) => `- Eliminado: ${o.nombre || o.descripcion || o.codigo || ''}`
              ),
          ]
            .filter(Boolean)
            .join('\n')
        : null;

      const body = {
        schema_name: getSchemaName(),
        idcliente: pedido.idcliente,
        tiene_modificaciones: tieneModificaciones,
        resumen_cambios: resumen || 'Productos actualizados.',
        condicion_venta: condicionVenta,
        costo_envio: costoEnvio,
      };
      const res = await API.put(`/pedidos/${pedido.id}/confirmar-revision`, body);
      try {
        sessionStorage.removeItem(`pedido_orig_${pedido.id}`);
      } catch {}
      const data = (res.data && res.data.data) || {};
      if (data.confirmacion_pendiente) {
        setConfirmacionEnviada(true);
      } else {
        setEstado(data.estado || 'esperando_pago');
      }
      setConfirmarPedido(false);
    } catch (e) {
      console.error('Error:', e);
      const detail = e?.response?.data?.detail || e?.response?.data?.message;
      alert(detail || 'Error al verificar pago. Intenta de nuevo.');
    }
    setActualizando(false);
  };

  const agregarProducto = (prod, cantidadSugerida) => {
    const stockReal = prod.stock ?? prod.n_cantidad ?? 0;
    const pesable = prod.pesable || false;
    const stockMax = pesable ? stockReal * 1000 : stockReal;
    const cantDefault = (() => {
      if (cantidadSugerida && cantidadSugerida > 0) {
        return stockMax > 0 ? Math.min(cantidadSugerida, stockMax) : cantidadSugerida;
      }
      const incDefault = pesable ? 1000 : 1;
      return stockMax > 0 ? Math.min(incDefault, stockMax) : incDefault;
    })();
    setProductos((prev) => {
      const existe = prev.find((p) => p.codigo === prod.codigo);
      const nuevos = existe
        ? prev.map((p) =>
            p.codigo === prod.codigo
              ? {
                  ...p,
                  cantidad: Math.min((p.cantidad || 1) + cantDefault, stockMax || Infinity),
                  stock: stockReal,
                  pesable,
                }
              : p
          )
        : [
            ...prev,
            {
              codigo: prod.codigo,
              nombre: prod.descripcion,
              precio: prod.precio,
              cantidad: cantDefault,
              pesable,
              stock: stockReal,
              referencia: prod.referencia || '',
            },
          ];
      programarAutoSave(nuevos);
      return nuevos;
    });
  };

  const modificarCantidad = (idx, delta) => {
    setProductos((prev) => {
      const nuevos = prev.map((p, i) => {
        if (i !== idx) return p;
        const stockMax = p.pesable ? (p.stock || 0) * 1000 : p.stock || 0;
        const limite =
          stockMax > 0 ? stockMax : delta < 0 ? (p.cantidad || 1) + delta : p.cantidad || 1;
        const nueva = Math.min(Math.max(1, (p.cantidad || 1) + delta), limite);
        return { ...p, cantidad: nueva };
      });
      programarAutoSave(nuevos);
      return nuevos;
    });
  };

  const manejarPesoChange = (idx, rawValue) => {
    const gramos = displayToGramos(rawValue);
    setPesosLocales((prev) => ({ ...prev, [idx]: rawValue }));
    if (gramos === null) return;
    setProductos((prev) => {
      const nuevos = prev.map((p, i) => {
        if (i !== idx) return p;
        const stockMax = p.pesable ? (p.stock || 0) * 1000 : p.stock || 0;
        const limite =
          stockMax > 0 ? stockMax : gramos > (p.cantidad || 1) ? p.cantidad || 1 : gramos;
        const cantidad = Math.min(gramos, limite);
        return { ...p, cantidad };
      });
      programarAutoSave(nuevos);
      return nuevos;
    });
  };

  const eliminarProducto = (idx) => {
    setProductos((prev) => {
      const nuevos = prev.filter((_, i) => i !== idx);
      programarAutoSave(nuevos);
      return nuevos;
    });
    setPesosLocales((prev) => {
      const n = {};
      Object.keys(prev).forEach((k) => {
        const ki = parseInt(k);
        if (ki < idx) n[ki] = prev[ki];
        else if (ki > idx) n[ki - 1] = prev[ki];
      });
      return n;
    });
  };

  const actualizarEtiquetas = (idx, etiquetas) => {
    setProductos((prev) => {
      const nuevos = prev.map((p, i) => {
        if (i !== idx) return p;
        const etiqExtra = (etiquetas || []).reduce((t, e) => t + (e.precio || 0), 0);
        return { ...p, etiquetas, etiquetas_extra: etiqExtra };
      });
      programarAutoSave(nuevos);
      return nuevos;
    });
  };

  const agregarCombo = (combo) => {
    setProductos((prev) => {
      const nuevos = [
        ...prev,
        {
          codigo: combo.codigo,
          combo_id: combo.combo_id,
          nombre: combo.nombre,
          precio: combo.precio,
          cantidad: 1,
          pesable: false,
          stock: 999999,
          imagen_url: combo.imagen_url || '',
          componentes: combo.componentes || [],
          combo_extra: combo.combo_extra || 0,
          etiquetas_extra: combo.combo_extra || 0,
        },
      ];
      programarAutoSave(nuevos);
      return nuevos;
    });
  };

  const abrirPersonalizarCombo = (idx, producto) => {
    setComboEdicion({ index: idx, producto });
  };

  const guardarPersonalizarCombo = (componentes, comboExtra) => {
    if (!comboEdicion) return;
    const idx = comboEdicion.index;
    setProductos((prev) => {
      const nuevos = prev.map((p, i) => {
        if (i !== idx) return p;
        return {
          ...p,
          componentes,
          combo_extra: comboExtra,
          etiquetas_extra: comboExtra,
        };
      });
      programarAutoSave(nuevos);
      return nuevos;
    });
    setComboEdicion(null);
  };

  const verificarPago = async (aprobado) => {
    setActualizando(true);
    try {
      const res = await API.put(`/pedidos/${pedido.id}/verificar-pago`, {
        schema_name: getSchemaName(),
        aprobado,
        motivo: aprobado ? '' : 'Pago rechazado',
      });
      if (res.data.status === 'success') {
        setEstado(aprobado ? res.data.data?.estado || 'armado' : 'esperando_pago');
      } else {
        setAvisoError(res.data.message || 'Error al verificar pago');
      }
    } catch (e) {
      console.error('Error:', e);
      const detail = e?.response?.data?.detail || e?.response?.data?.message;
      setAvisoError(detail || 'Error al verificar pago. Intenta de nuevo.');
    }
    setActualizando(false);
  };

  const cargarPagos = useCallback(async () => {
    if (!pedido?.id) return;
    setCargandoPagos(true);
    try {
      const r = await API.get(`/pedidos/${pedido.id}/pagos`);
      if (r.data.status === 'success') setPagos(r.data.data || []);
    } catch {
      setPagos([]);
    }
    setCargandoPagos(false);
  }, [pedido?.id]);

  useEffect(() => {
    cargarPagos();
  }, [estado, cargarPagos]);

  const verificarPagoItem = async (pagoId, aprobado) => {
    setPagoVerificandoId(pagoId);
    try {
      const res = await API.put(`/pedidos/${pedido.id}/pagos/${pagoId}/verificar`, {
        schema_name: getSchemaName(),
        aprobado,
        notas: aprobado ? '' : (pagoNotas[pagoId] || 'Pago rechazado'),
      });
      if (res.data.status === 'success') {
        const nuevoEstado = res.data.data?.estado_pedido;
        if (nuevoEstado) setEstado(nuevoEstado);
        cargarPagos();
      } else {
        setAvisoError(res.data.message || 'Error al verificar pago');
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.response?.data?.message;
      setAvisoError(detail || 'Error al verificar pago');
    }
    setPagoVerificandoId(null);
  };

  const agregarComprobantePago = async (pagoId, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      fd.append('schema_name', getSchemaName());
      const res = await API.post(`/pedidos/${pedido.id}/pagos/${pagoId}/comprobante`, fd);
      if (res.data.status === 'success') {
        cargarPagos();
        const nuevo = res.data.data;
        if (nuevo?.comprobante_url) {
          const p = pagos.find((x) => x.id === pagoId);
          if (p && !p.comprobante_url) setEstado('pago_en_verificacion');
        }
      }
    } catch (e) {
      setAvisoError(e?.response?.data?.detail || 'Error al subir comprobante');
    }
  };

  const eliminarPagoItem = async (pagoId) => {
    if (!window.confirm('¿Eliminar este pago?')) return;
    try {
      const res = await API.delete(`/pedidos/${pedido.id}/pagos/${pagoId}`);
      if (res.data.status === 'success') cargarPagos();
    } catch (e) {
      setAvisoError(e?.response?.data?.detail || 'Error al eliminar pago');
    }
  };

  const abrirCargarPago = async () => {
    setPagoLinea({ metodo_pago_id: '', monto: '', referencia: '', archivo: null, previewUrl: '' });
    setPagosBorrador([]);
    setMostrarCargarPago(true);
    if (metodosPago.length === 0) {
      try {
        const r = await API.get('/pedidos/metodos-pago');
        if (r.data.status === 'success') {
          const activos = (r.data.data || []).filter((m) => m.activo !== false);
          setMetodosPago(activos);
          if (activos.length > 0) {
            setPagoLinea((f) => ({ ...f, metodo_pago_id: String(activos[0].id) }));
          }
        }
      } catch {
        /* noop */
      }
    } else if (!pagoLinea.metodo_pago_id && metodosPago.length > 0) {
      setPagoLinea((f) => ({ ...f, metodo_pago_id: String(metodosPago[0].id) }));
    }
    if (monedas.length === 0) {
      await cargarMonedas();
    }
  };

  const cargarMonedas = useCallback(async () => {
    try {
      const r = await API.get('/ventas/monedas');
      if (r.data.status === 'success') {
        const mns = r.data.data || [];
        setMonedas(mns);
        const bs = mns.find((m) => ['VES', 'VEF', 'BS'].includes((m.codigo || '').toUpperCase()));
        if (!bs) {
          const t = await API.get('/pedidos/config/tasa');
          if (t.data.status === 'success' && t.data.data?.tasa_cambio) {
            setTasaBsFallback(parseFloat(t.data.data.tasa_cambio));
          }
        }
      }
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => {
    if (monedas.length === 0 && !tasaBsFallback) cargarMonedas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const monedaBs = monedas.find((m) => ['VES', 'VEF', 'BS'].includes((m.codigo || '').toUpperCase()));
  const tasaBs = (monedaBs && monedaBs.tasa > 0 ? monedaBs.tasa : null) || tasaBsFallback;
  const simboloBs = monedaBs?.simbolo || 'Bs.';
  const aBs = (usd) => (tasaBs ? Math.round(usd * tasaBs * 100) / 100 : null);
  const totalAPagar = parseFloat(pedido.monto_aprobado || pedido.total || 0) || 0;
  const totalLineas = pagosBorrador.reduce((s, l) => s + (parseFloat(l.monto) || 0), 0);
  const restantePago = Math.max(0, totalAPagar - totalLineas);

  const seleccionarArchivoPago = (file) => {
    if (pagoLinea.previewUrl) URL.revokeObjectURL(pagoLinea.previewUrl);
    const previewUrl = file && file.type.startsWith('image/') ? URL.createObjectURL(file) : '';
    setPagoLinea((f) => ({ ...f, archivo: file || null, previewUrl }));
  };

  const pegarImagenPago = async () => {
    try {
      if (!navigator.clipboard || !navigator.clipboard.read) {
        setAvisoError('Tu navegador no permite leer el portapapeles');
        return;
      }
      const items = await navigator.clipboard.read();
      for (const item of items) {
        const tipos = item.types || [];
        const imageType = tipos.find((t) => t.startsWith('image/'));
        if (imageType) {
          const blob = await item.getType(imageType);
          const ext = imageType.split('/')[1] || 'png';
          const file = new File([blob], `comprobante-${Date.now()}.${ext}`, { type: imageType });
          seleccionarArchivoPago(file);
          return;
        }
      }
      setAvisoError('No hay ninguna imagen en el portapapeles');
    } catch (e) {
      setAvisoError('No se pudo leer el portapapeles. Copia una imagen e intentalo de nuevo.');
    }
  };

  const agregarLineaPago = () => {
    if (!pagoLinea.metodo_pago_id) {
      setAvisoError('Selecciona un metodo de pago');
      return;
    }
    const monto = parseFloat(pagoLinea.monto);
    if (!monto || monto <= 0) {
      setAvisoError('Ingresa un monto mayor a 0');
      return;
    }
    const metodo = metodosPago.find((m) => String(m.id) === String(pagoLinea.metodo_pago_id));
    setPagosBorrador((prev) => [
      ...prev,
      {
        ...pagoLinea,
        metodo_nombre: metodo?.nombre || 'Metodo',
        monto: String(monto),
      },
    ]);
    setPagoLinea((f) => ({
      ...f,
      monto: '',
      referencia: '',
      archivo: null,
      previewUrl: '',
    }));
  };

  const quitarLineaPago = (idx) => {
    setPagosBorrador((prev) => {
      const linea = prev[idx];
      if (linea?.previewUrl) URL.revokeObjectURL(linea.previewUrl);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const cargarPagoManual = async () => {
    if (pagosBorrador.length === 0) {
      setAvisoError('Agrega al menos un pago');
      return;
    }
    setCargandoPago(true);
    let ultimoEstado = null;
    try {
      for (const linea of pagosBorrador) {
        const fd = new FormData();
        fd.append('metodo_pago_id', linea.metodo_pago_id);
        if (linea.monto) fd.append('monto', linea.monto);
        if (linea.referencia) fd.append('referencia', linea.referencia);
        if (linea.archivo) fd.append('archivo', linea.archivo);
        const res = await API.post(`/pedidos/${pedido.id}/pagos/manual`, fd);
        if (res.data.status !== 'success') {
          throw new Error(res.data.message || 'Error al cargar el pago');
        }
        ultimoEstado = res.data.data?.estado_pedido;
      }
      setMostrarCargarPago(false);
      pagosBorrador.forEach((l) => l.previewUrl && URL.revokeObjectURL(l.previewUrl));
      setPagosBorrador([]);
      if (ultimoEstado) setEstado(ultimoEstado);
      cargarPagos();
    } catch (e) {
      setAvisoError(e?.message || e?.response?.data?.detail || 'Error al cargar el pago');
    }
    setCargandoPago(false);
  };

  const devolverARevision = async () => {
    setActualizando(true);
    try {
      const res = await API.put(`/pedidos/${pedido.id}/devolver-a-revision`, {
        schema_name: getSchemaName(),
      });
      if (res.data.status === 'success') {
        setEstado('en_revision');
      } else {
        alert(res.data.message || 'Error al devolver a revision');
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.response?.data?.message;
      alert(detail || 'Error al devolver a revision');
    } finally {
      setActualizando(false);
      setConfirmarDevolverFinal(false);
    }
  };

  const abrirChatInterno = async () => {
    if (onChatAbrir) await onChatAbrir(pedido);
    setShowChat(true);
    setMobileTab('chat');
    if (onChatToggle) onChatToggle(true);
  };

  const cerrarChatInterno = () => {
    setShowChat(false);
    setMobileTab('pedido');
    setChatTexto('');
    setChatImagenesPreview([]);
    setChatImagenModalOpen(false);
    setChatDocPreview(null);
    setReplyingTo(null);
    if (onChatToggle) onChatToggle(false);
  };

  const handleEnviarChat = async (e) => {
    e.preventDefault();
    if (chatImagenesPreview.length > 0) {
      await subirImagenChat();
      return;
    }
    if (chatDocPreview) {
      await subirDocumentoChat();
      return;
    }
    if (!chatTexto.trim() || chatEnviando || !pedido.idcliente || !onChatEnviar) return;
    setChatEnviando(true);
    const msg = chatTexto.trim();
    const replyId = replyingTo ? replyingTo.id : null;
    setChatTexto('');
    setReplyingTo(null);
    try {
      await onChatEnviar(pedido.idcliente, msg, pedido.id, replyId);
    } catch (err) {
      console.error(err);
    }
    setChatEnviando(false);
    if (chatInputRef.current) chatInputRef.current.focus();
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  };

  const handleChatFileChange = (e) => {
    const files = Array.from(e.target.files || []).filter((f) =>
      f.type.startsWith('image/')
    );
    if (files.length > 0) {
      agregarChatImagenes(files);
    }
    if (chatFileInputRef.current) chatFileInputRef.current.value = '';
    if (chatCamaraInputRef.current) chatCamaraInputRef.current.value = '';
  };

  const agregarChatImagenes = (files) => {
    setChatImagenesPreview((prev) => [...prev, ...files]);
    setChatImagenSeleccionadaIdx(0);
    setChatImagenModalOpen(true);
  };

  const quitarChatImagen = (idx) => {
    setChatImagenesPreview((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      if (next.length === 0) {
        setChatImagenModalOpen(false);
      }
      return next;
    });
    setChatImagenSeleccionadaIdx((prev) => (prev >= idx && prev > 0 ? prev - 1 : prev));
  };

  const handleChatPasteImagenes = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const f = item.getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length > 0) {
      e.preventDefault();
      agregarChatImagenes(files);
    }
  };

  const cancelarChatImagenes = () => {
    setChatImagenesPreview([]);
    setChatImagenModalOpen(false);
    setChatImagenSeleccionadaIdx(0);
  };

  const handleChatDocChange = (e) => {
    const file = e.target.files[0];
    if (file) setChatDocPreview(file);
    if (chatDocInputRef.current) chatDocInputRef.current.value = '';
  };

  const subirDocumentoChat = async () => {
    if (!chatDocPreview || chatEnviando) return;
    setChatEnviando(true);
    try {
      const formData = new FormData();
      formData.append('file', chatDocPreview);
      formData.append('idcliente', pedido.idcliente);
      formData.append('schema_name', getSchemaName());
      formData.append('session_id', 'worker_upload');
      if (pedido.id) formData.append('pedido_id', pedido.id);
      if (replyingTo) formData.append('respuesta_a_id', replyingTo.id);
      setReplyingTo(null);
      await API.post('/chat/subir-documento', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (onChatRefresh) {
        setTimeout(() => onChatRefresh(pedido.idcliente, pedido.id), 500);
      }
    } catch (err) {
      console.error('Error subiendo documento:', err);
    } finally {
      setChatEnviando(false);
      setChatDocPreview(null);
    }
  };

  const subirImagenChat = async () => {
    if (chatImagenesPreview.length === 0 || chatEnviando) return;
    setChatEnviando(true);
    const cola = [...chatImagenesPreview];
    setChatImagenesPreview([]);
    setChatImagenModalOpen(false);
    setChatImagenSeleccionadaIdx(0);
    try {
      for (const imagen of cola) {
        const formData = new FormData();
        formData.append('file', imagen);
        formData.append('idcliente', pedido.idcliente);
        formData.append('schema_name', getSchemaName());
        formData.append('session_id', 'worker_upload');
        if (pedido.id) formData.append('pedido_id', pedido.id);
        if (replyingTo) formData.append('respuesta_a_id', replyingTo.id);
        await API.post('/chat/subir-imagen', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      setReplyingTo(null);
      if (onChatRefresh) {
        setTimeout(() => onChatRefresh(pedido.idcliente, pedido.id), 500);
      }
    } catch (err) {
      console.error('Error subiendo imagen:', err);
    } finally {
      setChatEnviando(false);
    }
  };

  const iniciarGrabacion = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      transcripcionRef.current = '';

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const transcripcion = transcripcionRef.current.trim();
        await subirAudio(blob, transcripcion);
      };

      mediaRecorder.start();

      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.lang = 'es-ES';
        recognition.interimResults = true;
        recognition.continuous = true;
        recognition.onresult = (e) => {
          let newFinal = '';
          for (let i = e.resultIndex; i < e.results.length; i++) {
            if (e.results[i].isFinal) {
              newFinal += ' ' + e.results[i][0].transcript;
            }
          }
          if (newFinal.trim()) {
            transcripcionRef.current = (transcripcionRef.current + ' ' + newFinal.trim()).trim();
          }
        };
        recognition.onerror = () => {};
        recognition.start();
        speechRef.current = recognition;
      }

      setGrabando(true);
    } catch (err) {
      console.error('Error accediendo al microfono:', err);
    }
  };

  const detenerGrabacion = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (speechRef.current) {
      try {
        speechRef.current.stop();
      } catch (e) {
        /* noop */
      }
      speechRef.current = null;
    }
    setGrabando(false);
  };

  const subirAudio = async (blob, transcripcion) => {
    if (!pedido.idcliente || chatEnviando) return;
    setChatEnviando(true);
    try {
      const formData = new FormData();
      formData.append('file', blob, 'audio.webm');
      formData.append('idcliente', pedido.idcliente);
      formData.append('schema_name', getSchemaName());
      formData.append('session_id', 'worker_upload');
      formData.append('transcripcion', transcripcion);
      if (pedido.id) formData.append('pedido_id', pedido.id);
      if (replyingTo) formData.append('respuesta_a_id', replyingTo.id);
      setReplyingTo(null);
      await API.post('/chat/subir-audio', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (onChatRefresh) {
        setTimeout(() => onChatRefresh(pedido.idcliente, pedido.id), 500);
      }
    } catch (err) {
      console.error('Error subiendo audio:', err);
    } finally {
      setChatEnviando(false);
    }
  };

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      if (speechRef.current) {
        try {
          speechRef.current.stop();
        } catch (e) {
          /* noop */
        }
        speechRef.current = null;
      }
    };
  }, []);

  const insertarEmoji = (emoji) => {
    setChatTexto((prev) => prev + emoji);
    setEmojiOpen(false);
    if (chatInputRef.current) chatInputRef.current.focus();
  };

  const estadoColor = {
    pendiente: '#fbbc04',
    en_revision: '#FF9500',
    esperando_pago: '#9c27b0',
    pago_en_verificacion: '#673ab7',
    pagado: '#0f9d58',
    armado: '#ff6d00',
    armado_cocina: '#ff6d00',
    listo: '#1a73e8',
    en_camino: '#00bcd4',
    llegada: '#009688',
    entregado: '#34a853',
    rechazado: '#ea4335',
    cancelado: '#9e9e9e',
  };
  const estadoLabel = {
    pendiente: 'Pendiente',
    en_revision: 'En Revision',
    esperando_pago: 'Esperando Pago',
    pago_en_verificacion: 'Verificando Pago',
    pagado: 'Pagado',
    armado: 'Armado',
    armado_cocina: 'Armado (Cocina)',
    listo: 'Listo',
    en_camino: 'En Camino',
    llegada: 'Llegada',
    entregado: 'Entregado',
    rechazado: 'Rechazado',
    cancelado: 'Cancelado',
  };
  const estadoIcon = {
    pendiente: <FiClock size={14} />,
    en_revision: <FiPackage size={14} />,
    esperando_pago: <FiDollarSign size={14} />,
    pago_en_verificacion: <FiCreditCard size={14} />,
    pagado: <FiShield size={14} />,
    armado: <FiPackage size={14} />,
    armado_cocina: <FiPackage size={14} />,
    listo: <FiTruck size={14} />,
    en_camino: <FiTruck size={14} />,
    llegada: <FiHome size={14} />,
    entregado: <FiCheckCircle size={14} />,
    rechazado: <FiXCircle size={14} />,
    cancelado: <FiXCircle size={14} />,
  };

  const sedeMostrar = pedido.sede_nombre || pedido.codigo_sede || '';

  const atendidoPorMi = pedido.atendido_por && pedido.atendido_por === usuario?.cedula;
  const nadieAtiende = !pedido.atendido_por;
  const readOnlyChat = !atendidoPorMi;
  const puedeGestionarPedidos = tienePermiso('pedidos.gestionar');
  const esTakeover = !nadieAtiende && readOnlyChat && puedeGestionarPedidos;
  const puedeTomar = nadieAtiende || esTakeover;
  const esEstadoFinal = ['entregado', 'rechazado', 'cancelado'].includes(estado);

  return (
    <div style={{ ...S.wrapper, ...(embedded ? S.wrapperEmbedded : {}) }}>
      <style>{`
        @media (max-width: 768px) {
          .pd-product-row { flex-wrap: wrap; gap: 6px; }
          .pd-product-detail { flex-direction: column; gap: 4px; }
          .pd-actions-row { flex-wrap: wrap; }
          .pd-actions-row button { font-size: 11px; padding: 6px 10px; }
        }
        @keyframes chatSlideIn { from { transform: translateX(30px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
      `}</style>
      {isMobile && showChat && (
        <div style={{
          padding: '8px 12px',
          backgroundColor: 'var(--agx-surface)',
          borderBottom: '1px solid var(--agx-border)',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          flexShrink: 0,
        }}>
          <button
            onClick={onBack}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '6px 8px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              color: 'var(--agx-muted)',
              fontSize: '12px',
              fontWeight: 500,
              borderRadius: '6px',
            }}
          >
            <FiArrowLeft size={16} /> Volver
          </button>
          <div style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: 'var(--agx-skeleton)',
            borderRadius: '8px',
            padding: '3px',
            gap: '3px',
          }}>
            <button
              onClick={() => setMobileTab('pedido')}
              style={{
                flex: 1,
                padding: '6px 10px',
                borderRadius: '6px',
                border: 'none',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                background: mobileTab === 'pedido' ? 'var(--agx-pri2)' : 'transparent',
                color: mobileTab === 'pedido' ? '#fff' : 'var(--agx-muted)',
                transition: 'all 0.15s',
              }}
            >
              Pedido
            </button>
            <button
              onClick={() => setMobileTab('chat')}
              style={{
                flex: 1,
                padding: '6px 10px',
                borderRadius: '6px',
                border: 'none',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                background: mobileTab === 'chat' ? 'var(--agx-pri2)' : 'transparent',
                color: mobileTab === 'chat' ? '#fff' : 'var(--agx-muted)',
                transition: 'all 0.15s',
              }}
            >
              Chat
            </button>
          </div>
          <button
            onClick={cerrarChatInterno}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '6px',
              display: 'flex',
              color: 'var(--agx-muted)',
              borderRadius: '6px',
              fontSize: '13px',
            }}
          >
            <FiX size={16} />
          </button>
        </div>
      )}
      <div className="pd-split" style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
        <div
          className="pd-split-detail"
          style={isMobile
            ? { display: mobileTab === 'pedido' ? 'flex' : 'none', flex: '1 1 0%', overflowY: 'auto', minWidth: 0, minHeight: 0, flexDirection: 'column' }
            : { flex: '1 1 55%', overflowY: 'auto', minWidth: 0, display: 'flex', flexDirection: 'column' }
          }>
        <div style={S.header}>
        {!embedded && (
          <button onClick={onBack} style={S.backBtn}>
            <FiArrowLeft size={20} />
          </button>
        )}
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: 'var(--agx-pri)' }}>
            Pedido #{pedido.id}
          </h2>
          {v('vis.pedidos.datos_cliente') && (
            <span style={{ fontSize: '12px', color: 'var(--agx-muted)' }}>
              {pedido.cliente} - {sedeMostrar}
            </span>
          )}
        </div>
        <span
          style={{
            ...S.badge,
            backgroundColor: estadoColor[estado] || '#fbbc04',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          {estadoIcon[estado] || <FiClock size={14} />}
          {estadoLabel[estado] || estado}
        </span>
      </div>

      <div
        style={{
          padding: '8px 16px',
          backgroundColor: 'var(--agx-surface)',
          borderBottom: '1px solid var(--agx-border)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          fontSize: '12px',
          color: 'var(--agx-muted)',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <strong>Sede:</strong> {sedeMostrar || 'N/A'}
        </div>
        {pedido.tipo_pedido === 'corporativo' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <strong>Cliente corporativo</strong>
            {pedido.sucursal_nombre ? ` · Sucursal: ${pedido.sucursal_nombre}` : ''}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <strong>Emision:</strong> {formatearFechaCompleta(pedido.fechapedido)}
        </div>
        {/* Tipo de entrega editable en revision (y siempre visible) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <strong>Entrega:</strong>
          {['pendiente', 'en_revision', 'listo'].includes(estado) && !readOnly ? (
            <select
              value={tipoEntrega || 'encomienda'}
              onChange={(e) => cambiarTipoEntrega(e.target.value)}
              style={{
                border: '1px solid var(--agx-border)',
                borderRadius: '6px',
                padding: '3px 6px',
                fontSize: '12px',
                background: 'var(--agx-surface)',
                color: 'var(--agx-text)',
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
            >
              <option value="encomienda">Encomienda</option>
              <option value="delivery">Delivery</option>
              <option value="retiro">Retiro</option>
            </select>
          ) : (
            <span>
              {tipoEntrega === 'retiro' ? 'Retiro' : tipoEntrega === 'delivery' ? 'Delivery' : 'Encomienda'}
            </span>
          )}
        </div>
        {v('vis.pedidos.direccion') && pedido.direccion_entrega && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <FiMapPin size={12} /> {pedido.direccion_entrega}
          </div>
        )}
      </div>

      <div style={S.body}>
        <h3 style={{
          fontSize: '13px',
          color: 'var(--agx-text)',
          margin: '0 0 8px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          Productos
          <span style={{
            backgroundColor: 'var(--agx-pri2)',
            color: '#fff',
            fontSize: '11px',
            fontWeight: 700,
            padding: '3px 10px',
            borderRadius: '10px',
            lineHeight: 1,
          }}>
            {productos.length}
          </span>
        </h3>
        {estado === 'en_revision' && !readOnly && (autoSaving || autoSaveError) && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 12px',
              borderRadius: '8px',
              marginBottom: '8px',
              fontSize: '12px',
              fontWeight: 500,
              backgroundColor: autoSaveError
                ? isDark()
                  ? '#7f1d1d'
                  : '#fef2f2'
                : isDark()
                  ? '#1e3a5f'
                  : '#dbeafe',
              color: autoSaveError
                ? isDark()
                  ? '#fca5a5'
                  : '#991b1b'
                : isDark()
                  ? '#93c5fd'
                  : '#1e40af',
            }}
          >
            {autoSaveError ? (
              <>
                <FiXCircle size={14} />
                <span style={{ flex: 1 }}>{autoSaveError}</span>
                <button
                  onClick={() => {
                    setAutoSaveError(null);
                    programarAutoSave(productos);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'inherit',
                    fontWeight: 600,
                    textDecoration: 'underline',
                    fontSize: '12px',
                  }}
                >
                  Reintentar
                </button>
              </>
            ) : (
              <>
                <span style={{
                  display: 'inline-block',
                  width: 14,
                  height: 14,
                  border: '2px solid currentColor',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'modalScaleIn 0.6s linear infinite',
                }} />
                <span>Guardando cambios...</span>
              </>
            )}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {productos.map((p, i) => (
            <ProductoItem
              key={i}
              producto={p}
              index={i}
              esEditable={estado === 'en_revision' && !readOnly}
              pesosLocales={pesosLocales}
              onCantidadChange={modificarCantidad}
              onPesoChange={manejarPesoChange}
              onEliminar={(idx) => setEliminandoIdx(idx)}
              onEtiquetasChange={actualizarEtiquetas}
              onPersonalizarCombo={abrirPersonalizarCombo}
            />
          ))}
        </div>

        {/* Condicion de venta (configurada por el asesor) */}
        {pedido.credito_habilitado !== false && (pedido.condicion_venta || estado === 'en_revision' || !readOnly) && (
          <div
            style={{
              marginTop: '12px',
              border: `1px solid ${condicionVenta === 'credito' ? '#ea4335' : '#137333'}`,
              borderRadius: '12px',
              overflow: 'hidden',
              background: 'var(--agx-surface)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 14px',
                background: condicionVenta === 'credito' ? '#fce8e6' : '#e6f4ea',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FiDollarSign size={16} color={condicionVenta === 'credito' ? '#ea4335' : '#137333'} />
                <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--agx-text)' }}>
                  Condición de venta
                </span>
              </div>
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  padding: '2px 10px',
                  borderRadius: '20px',
                  color: condicionVenta === 'credito' ? '#ea4335' : '#137333',
                  background: condicionVenta === 'credito' ? '#fff' : '#fff',
                }}
              >
                {condicionVenta === 'credito' ? 'Crédito' : 'Contado'}
              </span>
            </div>
            <div style={{ padding: '12px 14px', display: 'flex', gap: '10px' }}>
              {!readOnly && ['pendiente', 'en_revision'].includes(estado) ? (
                <>
                  <button
                    onClick={() => setCondicionVenta('contado')}
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '3px',
                      padding: '10px',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      border: condicionVenta === 'contado'
                        ? '2px solid #137333'
                        : '1px solid var(--agx-border)',
                      background: condicionVenta === 'contado' ? '#e6f4ea' : 'var(--agx-surface)',
                      transition: 'all 0.15s',
                    }}
                  >
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#137333' }}>
                      Contado
                    </span>
                    <span style={{ fontSize: '10px', color: 'var(--agx-muted)' }}>
                      Pago al confirmar
                    </span>
                  </button>
                  <button
                    onClick={() => setCondicionVenta('credito')}
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '3px',
                      padding: '10px',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      border: condicionVenta === 'credito'
                        ? '2px solid #ea4335'
                        : '1px solid var(--agx-border)',
                      background: condicionVenta === 'credito' ? '#fce8e6' : 'var(--agx-surface)',
                      transition: 'all 0.15s',
                    }}
                  >
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#ea4335' }}>
                      Crédito
                    </span>
                    <span style={{ fontSize: '10px', color: 'var(--agx-muted)' }}>
                      Pago después de la entrega
                    </span>
                  </button>
                </>
              ) : (
                <div style={{ fontSize: '12px', color: 'var(--agx-muted)', width: '100%', textAlign: 'center' }}>
                  {condicionVenta === 'credito'
                    ? 'Este pedido se gestiona a crédito.'
                    : 'Este pedido se gestiona de contado.'}
                </div>
              )}
            </div>
          </div>
        )}

        {estado === 'en_revision' && !readOnly && (
          <div style={{ marginTop: '10px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button onClick={() => setMostrarBuscador(true)} style={S.addProductBtn}>
              <FiSearch size={14} /> Agregar producto
            </button>
            <button onClick={() => setMostrarCombos(true)} style={S.addProductBtn}>
              <FiPackage size={14} /> Agregar combo
            </button>
          </div>
        )}

        <ProductoBuscadorModal
          open={mostrarBuscador}
          onClose={() => setMostrarBuscador(false)}
          sede={pedido.codigo_sede || '0101'}
          onAgregarProducto={agregarProducto}
          productosActuales={productos}
        />

        <ComboBuscadorModal
          open={mostrarCombos}
          onClose={() => setMostrarCombos(false)}
          onAgregarCombo={agregarCombo}
        />

        <ComboEdicionModal
          open={!!comboEdicion}
          onClose={() => setComboEdicion(null)}
          producto={comboEdicion?.producto}
          onGuardar={guardarPersonalizarCombo}
        />

        {v('vis.pedidos.total') && (
          <div style={{ marginTop: '10px' }}>
            {/* Costo de envio (delivery y encomienda, solo en revision) */}
            {estado === 'en_revision' && !readOnly && (pedido.tipo_entrega === 'delivery' || pedido.tipo_entrega === 'encomienda') && (
              <div style={{ ...S.totalRow, borderTop: '1px solid var(--agx-border)', marginTop: 0, padding: '6px 0' }}>
                <span>Costo de envio</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span>$</span>
                  <input type="number" step="0.1" min="0" value={costoEnvio || ''}
                    onChange={(e) => { setCostoEnvio(parseFloat(e.target.value) || 0); costoEnvioDirtyRef.current = true; }}
                    style={{
                      width: '70px', padding: '4px 6px', border: '1px solid var(--agx-border)',
                      borderRadius: '6px', fontSize: '13px', fontWeight: 600, color: 'var(--agx-text)',
                      fontFamily: 'inherit', textAlign: 'right', background: 'var(--agx-surface)',
                      outline: 'none',
                    }} />
                </div>
              </div>
            )}
            {/* Mostrar costo_envio cuando esta seteado pero no editable */}
            {(!(estado === 'en_revision' && !readOnly) && costoEnvio > 0) && (
              <div style={{ ...S.totalRow, borderTop: '1px solid var(--agx-border)', marginTop: 0, padding: '6px 0' }}>
                <span>Costo de envio</span>
                <span>${fmtMonto(costoEnvio)}</span>
              </div>
            )}
            <div style={{ ...S.totalRow, marginTop: 0, borderTop: '1px solid var(--agx-border)' }}>
              <span>Total productos</span>
              <span style={{ fontWeight: 600 }}>
                ${fmtMonto(total)}
                {tasaBs && (
                  <span style={{ color: 'var(--agx-muted)', fontWeight: 500, fontSize: '11px', marginLeft: '6px' }}>
                    ≈ {simboloBs}{fmtMonto(aBs(total))}
                  </span>
                )}
              </span>
            </div>
            <div style={{ ...S.totalRow, marginTop: 0, borderTop: '2px solid var(--agx-border)' }}>
              <span>Total a pagar</span>
              <span style={{ color: 'var(--agx-pri2)', fontWeight: 700, fontSize: '16px' }}>
                ${fmtMonto(total + costoEnvio)}
                {tasaBs && (
                  <span style={{ color: 'var(--agx-muted)', fontWeight: 600, fontSize: '12px', marginLeft: '6px' }}>
                    ≈ {simboloBs}{fmtMonto(aBs(total + costoEnvio))}
                  </span>
                )}
              </span>
            </div>
          </div>
        )}

        <div style={{ marginTop: '12px' }}>
          <h4 style={{ fontSize: '12px', color: 'var(--agx-text)', margin: '0 0 6px' }}>
            Informacion del cliente
          </h4>
          <div
            style={{
              backgroundColor: 'var(--agx-surface)',
              borderRadius: '8px',
              padding: '10px 14px',
              border: '1px solid var(--agx-border)',
              fontSize: '12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
            }}
          >
            {v('vis.pedidos.datos_cliente') && (
              <div>
                <strong>Cliente:</strong> {pedido.cliente || 'N/A'}
              </div>
            )}
            {v('vis.pedidos.datos_cliente') && pedido.telefono && (
              <div>
                <strong>Telefono:</strong>{' '}
                <a
                  href={`tel:${pedido.telefono}`}
                  style={{ color: 'var(--agx-pri2)', textDecoration: 'none', fontWeight: 600 }}
                >
                  {pedido.telefono}
                </a>
              </div>
            )}
            {v('vis.pedidos.direccion') && pedido.direccion_entrega && (
              <div>
                <strong>Direccion:</strong> {pedido.direccion_entrega}
              </div>
            )}
            {v('vis.pedidos.datos_cliente') && (
              <div>
                <strong>Sede:</strong> {sedeMostrar || 'N/A'}
              </div>
            )}
            {v('vis.pedidos.datos_cliente') && pedido.notas_asesor && (
              <div style={{ color: '#ea4335' }}>
                <strong>Notas:</strong> {pedido.notas_asesor}
              </div>
            )}
          </div>
          {v('vis.pedidos.direccion') && pedido.lat_entrega && pedido.lng_entrega && (
            <div style={{ marginTop: '8px' }}>
              <MapaUbicacion
                lat={pedido.lat_entrega}
                lng={pedido.lng_entrega}
                readOnly={true}
                height="140px"
              />
              <a
                href={`https://www.google.com/maps?q=${pedido.lat_entrega},${pedido.lng_entrega}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  marginTop: '6px',
                  padding: '8px',
                  borderRadius: '8px',
                  backgroundColor: 'var(--agx-bg2)',
                  border: '1px solid var(--agx-border)',
                  color: 'var(--agx-pri2)',
                  fontSize: '12px',
                  fontWeight: 600,
                  textDecoration: 'none',
                }}
              >
                <FiMapPin size={14} /> Abrir en Google Maps
              </a>
            </div>
          )}
        </div>

        {pagos.length > 0 &&
          v('vis.pedidos.comprobante') && (
            <div
              style={{
                marginTop: '12px',
                padding: '12px',
                backgroundColor: 'var(--agx-surface)',
                borderRadius: '8px',
                border: '1px solid var(--agx-border)',
              }}
            >
              <strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                Pagos del pedido
              </strong>
              {cargandoPagos && (
                <div style={{ fontSize: '11px', color: 'var(--agx-muted)' }}>Cargando pagos...</div>
              )}
              {!cargandoPagos && pagos.length === 0 && (
                <div style={{ fontSize: '12px', color: 'var(--agx-muted)' }}>
                  Sin pagos divididos registrados. Usa la verificación clásica abajo.
                </div>
              )}
              {pagos.map((p) => {
                const st =
                  p.estado === 'aprobado'
                    ? { label: 'Aprobado', c: '#34a853' }
                    : p.estado === 'rechazado'
                      ? { label: 'Rechazado', c: '#ea4335' }
                      : { label: 'Pendiente', c: '#f59e0b' };
                const comps =
                  p.comprobantes && p.comprobantes.length > 0
                    ? p.comprobantes
                    : p.comprobante_url
                      ? [{ url: p.comprobante_url }]
                      : [];
                return (
                  <div
                    key={p.id}
                    style={{
                      border: `1px solid ${st.c}40`,
                      borderRadius: '8px',
                      padding: '10px',
                      marginBottom: '8px',
                      background: p.estado === 'rechazado' ? 'var(--agx-red-bg)' : 'var(--agx-bg2)',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '13px' }}>{p.metodo_nombre || 'Método'}</div>
                        <div style={{ fontSize: '12px', color: 'var(--agx-muted)' }}>
                          ${fmtMonto(p.monto)}
                          {p.referencia ? ` · Ref: ${p.referencia}` : ''}
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: '11px',
                          fontWeight: 700,
                          padding: '2px 8px',
                          borderRadius: '6px',
                          background: st.c + '20',
                          color: st.c,
                        }}
                      >
                        {st.label}
                      </span>
                    </div>
                    {comps.map((c, i) => (
                      <img
                        key={i}
                        src={getImagenUrl(c.url)}
                        alt="Comprobante"
                        onClick={() => setImagenExpandida(true)}
                        style={{
                          width: '100%',
                          maxHeight: '200px',
                          objectFit: 'contain',
                          borderRadius: '6px',
                          marginTop: '8px',
                          border: '1px solid var(--agx-border)',
                          cursor: 'pointer',
                        }}
                      />
                    ))}
                    {p.notas && (
                      <div style={{ marginTop: '6px', fontSize: '11px', color: 'var(--agx-muted)' }}>
                        {p.notas}
                      </div>
                    )}
                    {(estado === 'pago_en_verificacion' || estado === 'esperando_pago') && (
                      <>
                        {estado === 'pago_en_verificacion' && p.estado !== 'aprobado' && (
                          <>
                            <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                              <button
                                onClick={() => verificarPagoItem(p.id, true)}
                                disabled={pagoVerificandoId === p.id}
                                style={{ flex: 1, padding: '7px', borderRadius: '6px', border: 'none', background: '#34a853', color: '#fff', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                              >
                                {pagoVerificandoId === p.id ? '...' : 'Aprobar'}
                              </button>
                              <button
                                onClick={() => verificarPagoItem(p.id, false)}
                                disabled={pagoVerificandoId === p.id}
                                style={{ flex: 1, padding: '7px', borderRadius: '6px', border: 'none', background: '#ea4335', color: '#fff', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                              >
                                Rechazar
                              </button>
                            </div>
                            <input
                              type="text"
                              placeholder="Nota (opcional)"
                              value={pagoNotas[p.id] || ''}
                              onChange={(e) => setPagoNotas((prev) => ({ ...prev, [p.id]: e.target.value }))}
                              style={{ width: '100%', marginTop: '6px', padding: '7px 10px', borderRadius: '6px', border: '1px solid var(--agx-border)', fontSize: '12px', background: 'var(--agx-surface)', color: 'var(--agx-text)' }}
                            />
                          </>
                        )}
                        <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                          <label
                            style={{ flex: 1, textAlign: 'center', padding: '6px', borderRadius: '6px', border: '1px solid var(--agx-border)', fontSize: '11px', fontWeight: 600, cursor: 'pointer', color: 'var(--agx-pri2)' }}
                          >
                            Adjuntar comprobante
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                const f = e.target.files[0];
                                if (f) agregarComprobantePago(p.id, f);
                              }}
                            />
                          </label>
                          <BotonFotoRemota
                            label="Foto celular"
                            style={{ padding: '6px 10px', fontSize: '11px' }}
                            params={{ destino: 'comprobante_pago', pedido_id: pedido.id, pago_id: p.id }}
                            onFoto={() => cargarPagos()}
                          />
                          <button
                            onClick={() => eliminarPagoItem(p.id)}
                            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--agx-border)', background: 'transparent', color: '#ea4335', fontSize: '11px', cursor: 'pointer' }}
                          >
                            Eliminar
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}

        {pedido.datos_pago &&
          (() => {
            let dp = pedido.datos_pago;
            if (typeof dp === 'string')
              try {
                dp = JSON.parse(dp);
              } catch {
                dp = null;
              }
            if (!dp) return null;
            return (
              <div
                style={{
                  marginTop: '12px',
                  padding: '12px',
                  backgroundColor: 'var(--agx-surface)',
                  borderRadius: '8px',
                  border: '1px solid var(--agx-border)',
                  fontSize: '12px',
                }}
              >
                <strong style={{ fontSize: '12px', display: 'block', marginBottom: '6px' }}>
                  Datos del pago
                </strong>
                {v('vis.pedidos.metodo_pago') && (
                  <div>
                    <strong>Metodo:</strong> {dp.metodo_nombre || pedido.metodo_pago}
                  </div>
                )}
                {dp.datos_cliente &&
                  typeof dp.datos_cliente === 'object' &&
                  Object.entries(dp.datos_cliente).map(([k, v]) => (
                    <div key={k} style={{ marginTop: '2px' }}>
                      <strong>{k}:</strong> {v}
                    </div>
                  ))}
              </div>
            );
          })()}

        {v('vis.pedidos.comprobante') && pagos.length === 0 && pedido.comprobante_url && (
          <div
            style={{
              marginTop: '12px',
              padding: '12px',
              backgroundColor: 'var(--agx-surface)',
              borderRadius: '8px',
              border: '1px solid var(--agx-border)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px',
              }}
            >
              <strong style={{ fontSize: '12px' }}>Comprobante de pago</strong>
              {v('vis.pedidos.metodo_pago') && (
                <span
                  style={{
                    fontSize: '11px',
                    padding: '2px 8px',
                    borderRadius: '6px',
                    backgroundColor: 'var(--agx-blue-bg)',
                    color: 'var(--agx-pri2)',
                  }}
                >
                  {pedido.metodo_pago}
                </span>
              )}
            </div>
            <div
              onClick={() => setImagenExpandida(true)}
              style={{
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid var(--agx-border)',
                cursor: 'pointer',
              }}
            >
              <img
                src={getImagenUrl(pedido.comprobante_url)}
                alt="Comprobante"
                style={{
                  width: '100%',
                  maxHeight: '240px',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </div>
            <div
              style={{
                fontSize: '10px',
                color: 'var(--agx-faint)',
                textAlign: 'center',
                marginTop: '4px',
              }}
            >
              Clic en la imagen para ampliar
            </div>
          </div>
        )}

        {historial.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            <h4 style={{ fontSize: '12px', color: 'var(--agx-text)', margin: '0 0 6px' }}>
              Historial
            </h4>
            <div
              style={{
                backgroundColor: 'var(--agx-surface)',
                borderRadius: '8px',
                padding: '10px 14px',
                border: '1px solid var(--agx-border)',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              {historial.map((h) => (
                <div key={h.id} style={{ display: 'flex', gap: '8px', fontSize: '12px' }}>
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: estadoColor[h.estado_nuevo] || '#9aa0a6',
                      marginTop: 4,
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: 'var(--agx-text)' }}>
                      <strong>{estadoLabel[h.estado_nuevo] || h.estado_nuevo}</strong>
                      {h.nota ? ` - ${h.nota}` : ''}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--agx-faint)' }}>
                      {formatearFechaCompleta(h.creado_en)}
                      {h.actor_nombre ? ` · ${h.actor_nombre}` : ''}
                      {h.actor_rol ? ` (${h.actor_rol})` : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {!readOnly && (
        <div style={{
          padding: '8px 16px',
          backgroundColor: 'var(--agx-surface)',
          borderTop: '1px solid var(--agx-border)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          flexShrink: 0,
          alignItems: 'center',
        }}>
          {(onChatAbrir || onIniciarChat) && !showChat && !['entregado', 'rechazado', 'cancelado'].includes(estado) && (
            <button
              onClick={() => {
                if (onChatAbrir) abrirChatInterno();
                else if (onIniciarChat) onIniciarChat(pedido);
              }}
              disabled={actualizando}
              style={{ ...S.actionBtn, backgroundColor: '#1a73e8' }}
            >
              <FiMessageCircle size={14} /> Chat
            </button>
          )}
          {onTomarChat && pedido.idcliente && puedeTomar && !esEstadoFinal && (
            <button
              onClick={async () => {
                setActualizando(true);
                const ok = await onTomarChat(pedido.idcliente);
                if (ok && onChatAbrir) abrirChatInterno();
                setActualizando(false);
              }}
              disabled={actualizando}
              style={{ ...S.actionBtn, backgroundColor: esTakeover ? '#ff6d00' : '#673ab7' }}
            >
              <FiShield size={14} /> {esTakeover ? 'Tomar chat (reasignar)' : 'Tomar chat'}
            </button>
          )}
          {onLiberarChat && pedido.idcliente && atendidoPorMi && !esEstadoFinal && (
            <button
              onClick={async () => {
                setActualizando(true);
                await onLiberarChat(pedido.idcliente);
                setActualizando(false);
              }}
              disabled={actualizando}
              style={{ ...S.actionBtn, backgroundColor: '#ff6d00' }}
            >
              <FiShield size={14} /> Liberar chat
            </button>
          )}
          {showChat && !isMobile && (
            <button
              onClick={cerrarChatInterno}
              disabled={actualizando}
              style={{ ...S.actionBtn, backgroundColor: '#9e9e9e' }}
            >
              <FiXCircle size={14} /> Ocultar chat
            </button>
          )}
          {estado === 'en_revision' && (
            <>
              {confirmacionEnviada && (
                <div
                  style={{
                    padding: '6px 12px',
                    backgroundColor: 'var(--agx-surface)',
                    borderRadius: '8px',
                    borderLeft: '3px solid var(--agx-pri2)',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    fontSize: '11px',
                    color: 'var(--agx-text)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    flexBasis: '100%',
                  }}
                >
                  <div
                    style={{
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      backgroundColor: isDark() ? 'rgba(96,165,250,0.15)' : 'rgba(41,82,204,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <FiClock size={12} color="var(--agx-pri2)" />
                  </div>
                  <span style={{ fontWeight: 600, color: 'var(--agx-pri)' }}>
                    Esperando confirmacion del cliente
                  </span>
                </div>
              )}
              {pedido.notas_asesor && (
                <div
                  style={{
                    fontSize: '11px',
                    color: 'var(--agx-muted)',
                    backgroundColor: 'var(--agx-bg2)',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    whiteSpace: 'pre-wrap',
                    flexBasis: '100%',
                  }}
                >
                  {pedido.notas_asesor}
                </div>
              )}
              {!confirmacionEnviada && puedeGestionarPedidos && (
                <>
                  <button
                    onClick={() => setConfirmarPedido(true)}
                    disabled={actualizando}
                    style={{ ...S.actionBtn, backgroundColor: '#34a853' }}
                  >
                    <FiCheckCircle size={14} /> Confirmar
                  </button>
                  <button
                    onClick={() => actualizarEstado('rechazado')}
                    disabled={actualizando}
                    style={{ ...S.actionBtn, backgroundColor: '#ea4335' }}
                  >
                    <FiXCircle size={14} /> Rechazar
                  </button>
                </>
              )}
              {!confirmacionEnviada && !puedeGestionarPedidos && (
                <div
                  style={{
                    fontSize: '11px',
                    color: 'var(--agx-muted)',
                    backgroundColor: 'var(--agx-bg2)',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    flexBasis: '100%',
                  }}
                >
                  Pedido en revisión. Solo un administrador puede confirmarlo.
                </div>
              )}
              {esFullChery && puedeGestionarPedidos && (
                <button
                  onClick={migrarAdvin}
                  disabled={actualizando || migrando || erpSync.estado === 'pendiente'}
                  style={{ ...S.actionBtn, backgroundColor: '#7c4dff' }}
                >
                  <FiPackage size={14} />{' '}
                  {migrando ? 'Migrando...' : erpSync.estado === 'pendiente' ? 'Enviando...' : erpSync.estado === 'procesado' ? 'Re-migrar a ADVIN' : 'Migrar a ADVIN'}
                </button>
              )}
              {esFullChery && erpSync.estado === 'pendiente' && (
                <div
                  style={{
                    padding: '6px 12px',
                    backgroundColor: 'var(--agx-surface)',
                    borderRadius: '8px',
                    borderLeft: '3px solid #7c4dff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    fontSize: '11px',
                    color: 'var(--agx-text)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    flexBasis: '100%',
                  }}
                >
                  <FiClock size={12} color="#7c4dff" />
                  <span style={{ fontWeight: 600 }}>Esperando confirmación de sincronización…</span>
                </div>
              )}
              {esFullChery && erpSync.estado === 'procesado' && (
                <div
                  style={{
                    padding: '6px 12px',
                    backgroundColor: 'var(--agx-surface)',
                    borderRadius: '8px',
                    borderLeft: '3px solid #34a853',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    fontSize: '11px',
                    color: 'var(--agx-text)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    flexBasis: '100%',
                  }}
                >
                  <FiCheckCircle size={12} color="#34a853" />
                  <span style={{ fontWeight: 600 }}>
                    Factura en espera Nº {erpSync.numfac || '—'} creada en ADVIN
                  </span>
                </div>
              )}
              {esFullChery && erpSync.estado === 'error' && (
                <div
                  style={{
                    padding: '6px 12px',
                    backgroundColor: 'var(--agx-surface)',
                    borderRadius: '8px',
                    borderLeft: '3px solid #ea4335',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    fontSize: '11px',
                    color: 'var(--agx-text)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    flexBasis: '100%',
                    flexWrap: 'wrap',
                  }}
                >
                  <FiAlertTriangle size={12} color="#ea4335" />
                  <span style={{ fontWeight: 600, color: '#ea4335' }}>
                    Error al sincronizar: {erpSync.error || 'desconocido'}
                  </span>
                  {puedeGestionarPedidos && (
                    <button
                      onClick={migrarAdvin}
                      disabled={migrando}
                      style={{ ...S.actionBtn, backgroundColor: '#ea4335', marginLeft: 'auto' }}
                    >
                      <FiRotateCcw size={14} /> Reintentar
                    </button>
                  )}
                </div>
              )}
              {esFullChery && avisoErp && (
                <div
                  style={{
                    padding: '6px 12px',
                    backgroundColor: 'var(--agx-surface)',
                    borderRadius: '8px',
                    borderLeft: '3px solid #ea4335',
                    fontSize: '11px',
                    color: '#ea4335',
                    flexBasis: '100%',
                  }}
                >
                  {avisoErp}
                </div>
              )}
              {puedeGestionarPedidos && (
                <button
                  onClick={() => actualizarEstado('pendiente')}
                  disabled={actualizando}
                  style={{ ...S.actionBtn, backgroundColor: '#607d8b' }}
                >
                  <FiRotateCcw size={14} /> Regresar a pendiente
                </button>
              )}
            </>
          )}
          {estado === 'pago_en_verificacion' && pagos.length === 0 && (
            <>
              <button
                onClick={() => setConfirmarVerificacion(true)}
                disabled={actualizando}
                style={{ ...S.actionBtn, backgroundColor: '#34a853' }}
              >
                <FiCheckCircle size={14} /> Pago Correcto
              </button>
              <button
                onClick={() => setConfirmarVerificacion(false)}
                disabled={actualizando}
                style={{ ...S.actionBtn, backgroundColor: '#ea4335' }}
              >
                <FiXCircle size={14} /> Pago Incorrecto
              </button>
            </>
          )}
          {estado === 'pendiente' && (
            <button
              onClick={() => actualizarEstado('en_revision')}
              disabled={actualizando}
              style={{ ...S.actionBtn, backgroundColor: '#34a853' }}
            >
              <FiCheckCircle size={14} /> Confirmar pedido
            </button>
          )}
          {['pendiente', 'en_revision', 'aprobado', 'esperando_pago', 'pago_en_verificacion'].includes(estado) && puedeGestionarPedidos && (
            <button
              onClick={abrirCargarPago}
              disabled={actualizando}
              style={{ ...S.actionBtn, backgroundColor: '#673ab7' }}
            >
              <FiCreditCard size={14} /> Cargar pago
            </button>
          )}
          {['esperando_pago', 'aprobado', 'pago_en_verificacion'].includes(estado) && (
            <button
              onClick={() => actualizarEstado('en_revision')}
              disabled={actualizando}
              style={{ ...S.actionBtn, backgroundColor: '#ff6d00' }}
            >
              <FiRotateCcw size={14} /> Volver a revision
            </button>
          )}
          {estado === 'armado' && (
            <>
              <button
                onClick={async () => {
                  try {
                    await API.put(`/pedidos/${pedido.id}/marcar-listo`, {
                      schema_name: getSchemaName(),
                    });
                    setEstado('listo');
                  } catch (e) {
                    console.error('Error:', e);
                  }
                }}
                disabled={actualizando}
                style={{ ...S.actionBtn, backgroundColor: '#1a73e8' }}
              >
                <FiTruck size={14} /> Marcar como listo
              </button>
              {condicionVenta === 'credito' && (
                <button
                  onClick={() => {
                    setConfirmarDevolver(true);
                  }}
                  disabled={actualizando}
                  style={{ ...S.actionBtn, backgroundColor: '#ea4335' }}
                >
                  <FiRotateCcw size={14} /> Devolver a revision
                </button>
              )}
            </>
          )}
          {estado === 'listo' && tipoEntrega === 'retiro' && (
            <button
              onClick={async () => {
                try {
                  await API.put(`/pedidos/${pedido.id}/marcar-retirado`, {
                    schema_name: getSchemaName(),
                  });
                  setEstado('entregado');
                } catch (e) {
                  console.error('Error:', e);
                }
              }}
              disabled={actualizando}
              style={{ ...S.actionBtn, backgroundColor: '#34a853' }}
            >
              <FiCheckCircle size={14} /> Entregar (retiro)
            </button>
          )}
          {estado === 'listo' && tipoEntrega === 'encomienda' && (
            <button
              onClick={() => setConfirmarSalioLocal(true)}
              disabled={actualizando}
              style={{ ...S.actionBtn, backgroundColor: '#00897b' }}
            >
              <FiTruck size={14} /> Salio del local
            </button>
          )}
          {estado === 'listo' && tipoEntrega === 'delivery' && (
            <button
              onClick={() => actualizarEstado('en_camino')}
              disabled={actualizando}
              style={{ ...S.actionBtn, backgroundColor: '#00bcd4' }}
            >
              <FiTruck size={14} /> Marcar en camino
            </button>
          )}
          {estado === 'listo' && condicionVenta === 'credito' && (
            <button
              onClick={() => setConfirmarDevolver(true)}
              disabled={actualizando}
              style={{ ...S.actionBtn, backgroundColor: '#ea4335' }}
            >
              <FiRotateCcw size={14} /> Devolver a revision
            </button>
          )}
          {estado === 'despachado' && (
            <button
              onClick={() => setConfirmarEntregadoEncomienda(true)}
              disabled={actualizando}
              style={{ ...S.actionBtn, backgroundColor: '#34a853' }}
            >
              <FiCheckCircle size={14} /> Marcar como entregado
            </button>
          )}
          {(estado === 'en_camino' || estado === 'despachado') && (
            <button
              onClick={() => actualizarEstado('listo')}
              disabled={actualizando}
              style={{ ...S.actionBtn, backgroundColor: '#ff6d00' }}
            >
              <FiRotateCcw size={14} /> Volver a listo
            </button>
          )}
          <div style={{ flex: '1 1 auto' }} />
          {!['entregado', 'rechazado', 'cancelado'].includes(estado) && (
            <button
              onClick={() => {
                setMotivoCancelacion('');
                setConfirmarCancelar(true);
              }}
              disabled={actualizando}
              style={{ ...S.actionBtn, backgroundColor: 'transparent', color: '#9e9e9e', border: '1px solid var(--agx-border)' }}
            >
              <FiXCircle size={14} /> Cancelar
            </button>
          )}
        </div>
      )}
        </div>
        {((!isMobile && showChat) || (isMobile && showChat && mobileTab === 'chat')) ? (
          <div
            className="pd-split-chat"
            style={
              isMobile
                ? {
                    flex: '1 1 0%',
                    maxWidth: '100%',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderLeft: 'none',
                    overflow: 'hidden',
                  }
                : {
                    flex: '0 0 420px',
                    maxWidth: '45%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderLeft: '1px solid var(--agx-border)',
                    overflow: 'hidden',
                    animation: 'chatSlideIn 0.2s ease',
                  }
            }
          >
            <div style={S.chatHeader}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--agx-text)' }}>
                  Chat <span style={{ color: 'var(--agx-pri2)' }}>#{pedido.id}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--agx-muted)', marginTop: '1px' }}>
                  <span>{pedido.cliente || 'Cliente'}</span>
                  {pedido.nombre_asesor ? (
                    <span style={{
                      padding: '0px 6px',
                      borderRadius: '6px',
                      backgroundColor: 'var(--agx-green-bg)',
                      color: 'var(--agx-green)',
                      fontSize: '10px',
                      fontWeight: 600,
                    }}>
                      Atendido por: {pedido.nombre_asesor}
                    </span>
                  ) : pedido.atendido_por ? (
                    <span style={{
                      padding: '0px 6px',
                      borderRadius: '6px',
                      backgroundColor: 'var(--agx-green-bg)',
                      color: 'var(--agx-green)',
                      fontSize: '10px',
                      fontWeight: 600,
                    }}>
                      Atendido por: {pedido.atendido_por}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--agx-faint)', fontSize: '10px' }}>
                      Bot activo
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={cerrarChatInterno}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--agx-muted)',
                  padding: '4px',
                  display: 'flex',
                  borderRadius: '8px',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--agx-skeleton)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
              >
                <FiX size={18} />
              </button>
            </div>
            <div ref={chatMessagesRef} style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {readOnlyChat && (
                <div style={{
                  padding: '6px 12px',
                  backgroundColor: isDark() ? '#78350f' : '#fffbeb',
                  color: isDark() ? '#fcd34d' : '#92400e',
                  fontSize: '11px',
                  fontWeight: 500,
                  textAlign: 'center',
                  borderBottom: '1px solid var(--agx-border)',
                  flexShrink: 0,
                }}>
                  {nadieAtiende
                    ? 'Debes tomar el chat para enviar mensajes.'
                    : esTakeover
                      ? `Chat atendido por ${pedido.nombre_asesor || pedido.atendido_por}. Puedes tomarlo para responder.`
                      : `Chat atendido por ${pedido.nombre_asesor || pedido.atendido_por}. Solo lectura.`}
                </div>
              )}
              <MessageList
                mensajes={chatMensajes}
                onLoadMore={onChatCargarMas}
                hasMore={chatHasMore}
                loadingMore={chatCargandoMas}
                shouldScrollToBottom={true}
                onReply={(msg) => setReplyingTo(msg)}
              />
            </div>
            {replyingTo && (
              <div
                style={{
                  padding: '6px 16px',
                  backgroundColor: 'var(--agx-skeleton)',
                  borderTop: '1px solid var(--agx-border)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexShrink: 0,
                }}
              >
                <div style={{ flex: 1, minWidth: 0, borderLeft: '3px solid #1a73e8', paddingLeft: '8px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--agx-pri2)' }}>
                    Respondiendo a{' '}
                    {replyingTo.autor === 'cliente'
                      ? 'cliente'
                      : replyingTo.autor === 'bot'
                        ? 'bot'
                        : 'asesor'}
                  </div>
                  <div
                    style={{
                      fontSize: '12px',
                      color: 'var(--agx-muted)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {replyingTo.tipo === 'imagen'
                      ? '📷 Imagen'
                      : replyingTo.tipo === 'audio'
                        ? '🎤 Audio'
                        : replyingTo.mensaje}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setReplyingTo(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--agx-muted)',
                    fontSize: '18px',
                    lineHeight: 1,
                    padding: '0 2px',
                  }}
                >
                  ×
                </button>
              </div>
            )}
            <form
              onSubmit={handleEnviarChat}
              style={{
                padding: '10px 16px',
                backgroundColor: 'var(--agx-surface)',
                borderTop: '1px solid var(--agx-border)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flexShrink: 0,
                position: 'relative',
              }}
            >
              <input
                type="file"
                ref={chatFileInputRef}
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={handleChatFileChange}
              />
              <input
                type="file"
                ref={chatCamaraInputRef}
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={handleChatFileChange}
              />
              <input
                type="file"
                ref={chatDocInputRef}
                accept="application/pdf,.pdf"
                style={{ display: 'none' }}
                onChange={handleChatDocChange}
              />
              {chatDocPreview ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
                  <FiFileText size={18} color="#ea4335" />
                  <span
                    style={{
                      fontSize: '11px',
                      color: 'var(--agx-muted)',
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {chatDocPreview.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => setChatDocPreview(null)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#ea4335',
                      fontSize: '16px',
                      padding: '0 2px',
                    }}
                  >
                    x
                  </button>
                  <button
                    type="button"
                    onClick={subirDocumentoChat}
                    disabled={chatEnviando}
                    style={{
                      padding: '4px 10px',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: 'var(--agx-pri2)',
                      color: '#fff',
                      fontSize: '11px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Enviar
                  </button>
                </div>
              ) : chatImagenesPreview.length > 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
                  <span
                    style={{
                      fontSize: '11px',
                      color: 'var(--agx-muted)',
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {chatImagenesPreview.length} imagen{chatImagenesPreview.length > 1 ? 'es' : ''} lista{chatImagenesPreview.length > 1 ? 's' : ''}
                  </span>
                  <button
                    type="button"
                    onClick={() => setChatImagenModalOpen(true)}
                    style={{
                      padding: '4px 10px',
                      borderRadius: '6px',
                      border: '1px solid var(--agx-border)',
                      background: 'transparent',
                      color: 'var(--agx-text)',
                      fontSize: '11px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Ver
                  </button>
                  <button
                    type="button"
                    onClick={cancelarChatImagenes}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#ea4335',
                      fontSize: '16px',
                      padding: '0 2px',
                    }}
                  >
                    x
                  </button>
                  <button
                    type="button"
                    onClick={subirImagenChat}
                    disabled={chatEnviando}
                    style={{
                      padding: '4px 10px',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: 'var(--agx-pri2)',
                      color: '#fff',
                      fontSize: '11px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Enviar
                  </button>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setEmojiOpen((o) => !o);
                      setAttachMenuOpen(false);
                    }}
                    disabled={readOnlyChat}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: readOnlyChat ? 'default' : 'pointer',
                      padding: '4px',
                      display: 'flex',
                      color: 'var(--agx-muted)',
                      opacity: readOnlyChat ? 0.3 : 1,
                    }}
                    aria-label="Emojis"
                  >
                    <FiSmile size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAttachMenuOpen((o) => !o);
                      setEmojiOpen(false);
                    }}
                    disabled={readOnlyChat}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: readOnlyChat ? 'default' : 'pointer',
                      padding: '4px',
                      display: 'flex',
                      color: 'var(--agx-muted)',
                      opacity: readOnlyChat ? 0.3 : 1,
                    }}
                    aria-label="Adjuntar"
                  >
                    <FiPaperclip size={18} />
                  </button>

                  {attachMenuOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '56px',
                        left: '60px',
                        backgroundColor: 'var(--agx-surface)',
                        border: '1px solid var(--agx-border)',
                        borderRadius: '12px',
                        boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                        overflow: 'hidden',
                        zIndex: 50,
                        minWidth: '190px',
                      }}
                    >
                      {[
                        { label: 'Galería', icon: <FiImage size={18} />, action: () => { chatFileInputRef.current?.click(); setAttachMenuOpen(false); } },
                        { label: 'Cámara', icon: <FiCamera size={18} />, action: () => { chatCamaraInputRef.current?.click(); setAttachMenuOpen(false); } },
                        { label: 'Documento PDF', icon: <FiFileText size={18} />, action: () => { chatDocInputRef.current?.click(); setAttachMenuOpen(false); } },
                      ].map((op) => (
                        <button
                          key={op.label}
                          type="button"
                          onClick={op.action}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '11px 16px',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--agx-text)',
                            fontSize: '13px',
                            fontWeight: 500,
                            textAlign: 'left',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--agx-skeleton)')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                        >
                          {op.icon} {op.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {emojiOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '56px',
                        left: '12px',
                        backgroundColor: 'var(--agx-surface)',
                        border: '1px solid var(--agx-border)',
                        borderRadius: '12px',
                        boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                        zIndex: 50,
                        padding: '8px',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(8, 1fr)',
                        gap: '2px',
                        width: '256px',
                      }}
                    >
                      {EMOJIS.map((em) => (
                        <button
                          key={em}
                          type="button"
                          onClick={() => insertarEmoji(em)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '20px',
                            padding: '4px',
                            borderRadius: '6px',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--agx-skeleton)')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                        >
                          {em}
                        </button>
                      ))}
                    </div>
                  )}

                  <input
                    ref={chatInputRef}
                    type="text"
                    value={chatTexto}
                    onChange={(e) => setChatTexto(e.target.value)}
                    onPaste={handleChatPasteImagenes}
                    onFocus={() => {
                      setAttachMenuOpen(false);
                      setEmojiOpen(false);
                    }}
                    disabled={readOnlyChat}
                    placeholder={readOnlyChat
                      ? (nadieAtiende ? 'Debes tomar el chat primero' : `Chat atendido por ${pedido.nombre_asesor || pedido.atendido_por}`)
                      : 'Escribe un mensaje...'}
                    style={{
                      flex: 1,
                      padding: '8px 14px',
                      border: '1px solid var(--agx-border)',
                      borderRadius: '20px',
                      fontSize: '13px',
                      outline: 'none',
                      fontFamily: 'inherit',
                      backgroundColor: 'var(--agx-input-bg)',
                      color: 'var(--agx-text)',
                    }}
                  />

                  {grabando ? (
                    <button
                      type="button"
                      onClick={detenerGrabacion}
                      disabled={readOnlyChat}
                      style={{
                        background: 'var(--agx-pri2)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '36px',
                        height: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: readOnlyChat ? 'default' : 'pointer',
                        color: '#fff',
                        padding: 0,
                        flexShrink: 0,
                        animation: 'pulse 1.2s infinite',
                      }}
                      aria-label="Detener grabacion"
                    >
                      <FiSquare size={14} />
                    </button>
                  ) : chatTexto.trim() ? (
                    <button
                      type="submit"
                      disabled={readOnlyChat || !chatTexto.trim() || chatEnviando}
                      style={{
                        background: 'var(--agx-pri2)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '36px',
                        height: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: readOnlyChat ? 'default' : 'pointer',
                        color: '#fff',
                        padding: 0,
                        flexShrink: 0,
                        opacity: readOnlyChat ? 0.3 : (chatTexto.trim() ? 1 : 0.4),
                      }}
                    >
                      <FiSend size={16} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={iniciarGrabacion}
                      disabled={readOnlyChat}
                      style={{
                        background: 'var(--agx-pri2)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '36px',
                        height: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: readOnlyChat ? 'default' : 'pointer',
                        color: '#fff',
                        padding: 0,
                        flexShrink: 0,
                        opacity: readOnlyChat ? 0.3 : 1,
                      }}
                      aria-label="Grabar audio"
                    >
                      <FiMic size={16} />
                    </button>
                  )}
                </>
              )}
            </form>
          </div>
        ) : null}
      </div>

      {chatImagenModalOpen && chatImagenesPreview.length > 0 && (
        <div
          onClick={cancelarChatImagenes}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.85)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 3000,
            padding: '20px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: isDark() ? '#1E293B' : '#1E293B',
              borderRadius: '20px',
              padding: '24px',
              maxWidth: '520px',
              width: '100%',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 16px 48px rgba(0,0,0,0.4)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <h3 style={{ margin: 0, fontSize: '16px', color: '#F1F5F9', fontWeight: 600 }}>
                Vista previa {chatImagenesPreview.length > 1 ? `(${chatImagenesPreview.length})` : ''}
              </h3>
              <button
                onClick={cancelarChatImagenes}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#94A3B8',
                  padding: '4px',
                  fontSize: '20px',
                }}
              >
                {'\u2715'}
              </button>
            </div>
            <div
              style={{
                borderRadius: '12px',
                overflow: 'hidden',
                marginBottom: '12px',
                backgroundColor: '#0F172A',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src={URL.createObjectURL(chatImagenesPreview[chatImagenSeleccionadaIdx])}
                alt="Preview"
                style={{
                  width: '100%',
                  maxHeight: '360px',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </div>
            {chatImagenesPreview.length > 1 && (
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  overflowX: 'auto',
                  paddingBottom: '8px',
                  marginBottom: '12px',
                }}
              >
                {chatImagenesPreview.map((img, i) => (
                  <div key={i} style={{ position: 'relative', flexShrink: 0 }}>
                    <img
                      src={URL.createObjectURL(img)}
                      alt={`Miniatura ${i + 1}`}
                      onClick={() => setChatImagenSeleccionadaIdx(i)}
                      style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '8px',
                        objectFit: 'cover',
                        cursor: 'pointer',
                        border:
                          i === chatImagenSeleccionadaIdx
                            ? '2px solid var(--agx-pri2)'
                            : '2px solid transparent',
                      }}
                    />
                    <button
                      onClick={() => quitarChatImagen(i)}
                      style={{
                        position: 'absolute',
                        top: '-6px',
                        right: '-6px',
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        border: 'none',
                        background: '#ef4444',
                        color: '#fff',
                        fontSize: '11px',
                        lineHeight: 1,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      aria-label="Quitar imagen"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div
              style={{
                fontSize: '12px',
                color: '#94A3B8',
                marginBottom: '16px',
                textAlign: 'center',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {chatImagenesPreview[chatImagenSeleccionadaIdx].name}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={cancelarChatImagenes}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '12px',
                  border: '1px solid #334155',
                  backgroundColor: 'transparent',
                  color: '#94A3B8',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancelar
              </button>
              <button
                onClick={subirImagenChat}
                disabled={chatEnviando}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '12px',
                  border: 'none',
                  backgroundColor: 'var(--agx-pri2)',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                <FiSend size={14} /> Enviar{chatImagenesPreview.length > 1 ? ` (${chatImagenesPreview.length})` : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      <Modal
        open={eliminandoIdx !== null}
        onClose={() => setEliminandoIdx(null)}
        width="sm"
        title={null}
      >
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              backgroundColor: isDark() ? 'rgba(234,67,53,0.15)' : '#fef2f2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 14px',
            }}
          >
            <FiAlertTriangle size={28} color="#ea4335" />
          </div>
          <h3 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 700 }}>
            Eliminar producto
          </h3>
          <p style={{ fontSize: '13px', color: 'var(--agx-muted)', margin: '0 0 20px' }}>
            Esta seguro de eliminar{' '}
            <strong>
              &laquo;{eliminandoIdx !== null ? (productos[eliminandoIdx]?.nombre || productos[eliminandoIdx]?.descripcion || 'este producto') : ''}&raquo;
            </strong>{' '}
            del pedido?
          </p>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => setEliminandoIdx(null)}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '10px',
                border: '1px solid var(--agx-border)',
                backgroundColor: 'var(--agx-surface)',
                color: 'var(--agx-muted)',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              onClick={() => {
                const idx = eliminandoIdx;
                setEliminandoIdx(null);
                eliminarProducto(idx);
              }}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '10px',
                border: 'none',
                backgroundColor: '#ea4335',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Eliminar
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={confirmarVerificacion !== null}
        onClose={() => setConfirmarVerificacion(null)}
        width="sm"
        title={null}
      >
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          {confirmarVerificacion ? (
            <>
              <FiCheckCircle size={40} color="#34a853" style={{ marginBottom: '12px' }} />
              <h3 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 700 }}>
                Confirmar pago
              </h3>
              <p style={{ fontSize: '13px', color: 'var(--agx-muted)', margin: '0 0 16px' }}>
                El pedido pasara a fase de armado.
              </p>
            </>
          ) : (
            <>
              <FiXCircle size={40} color="#ea4335" style={{ marginBottom: '12px' }} />
              <h3 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 700 }}>
                Rechazar pago
              </h3>
              <p style={{ fontSize: '13px', color: 'var(--agx-muted)', margin: '0 0 16px' }}>
                El cliente debera reenviar el comprobante.
              </p>
            </>
          )}
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => setConfirmarVerificacion(null)}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '10px',
                border: '1px solid var(--agx-border)',
                backgroundColor: 'var(--agx-surface)',
                color: 'var(--agx-muted)',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              onClick={async () => {
                const a = confirmarVerificacion;
                setConfirmarVerificacion(null);
                await verificarPago(a);
              }}
              disabled={actualizando}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '10px',
                border: 'none',
                backgroundColor: confirmarVerificacion ? '#34a853' : '#ea4335',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {confirmarVerificacion ? 'Confirmar' : 'Rechazar'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!avisoError}
        onClose={() => setAvisoError(null)}
        width="sm"
        title={null}
      >
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <FiAlertTriangle size={40} color="#f59e0b" style={{ marginBottom: '12px' }} />
          <h3 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 700 }}>
            No se pudo verificar el pago
          </h3>
          <p style={{ fontSize: '13px', color: 'var(--agx-muted)', margin: '0 0 16px', lineHeight: 1.5 }}>
            {avisoError}
          </p>
          <button
            onClick={() => setAvisoError(null)}
            style={{
              width: '100%',
              padding: '11px',
              borderRadius: '10px',
              border: 'none',
              backgroundColor: 'var(--agx-pri2, #2563eb)',
              color: '#fff',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Entendido
          </button>
        </div>
      </Modal>

      <Modal
        open={mostrarCargarPago}
        onClose={() => setMostrarCargarPago(false)}
        width="lg"
        title="Cargar pago"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
              padding: '12px 14px',
              borderRadius: '10px',
              background: 'var(--agx-bg2)',
              border: '1px solid var(--agx-border)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
              <span style={{ color: 'var(--agx-muted)' }}>Total a pagar</span>
              <strong style={{ color: 'var(--agx-text)' }}>
                ${fmtMonto(totalAPagar)}
                {tasaBs && (
                  <span style={{ color: 'var(--agx-muted)', fontWeight: 500 }}>
                    {' '}≈ {simboloBs}{fmtMonto(aBs(totalAPagar))}
                  </span>
                )}
              </strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
              <span style={{ color: 'var(--agx-muted)' }}>Cubierto</span>
              <strong style={{ color: '#34a853' }}>
                ${fmtMonto(totalLineas)}
                {tasaBs && (
                  <span style={{ fontWeight: 500 }}>
                    {' '}≈ {simboloBs}{fmtMonto(aBs(totalLineas))}
                  </span>
                )}
              </strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
              <span style={{ color: 'var(--agx-muted)' }}>Restante</span>
              <strong style={{ color: restantePago > 0.009 ? '#f59e0b' : '#34a853' }}>
                ${fmtMonto(restantePago)}
                {tasaBs && (
                  <span style={{ fontWeight: 500 }}>
                    {' '}≈ {simboloBs}{fmtMonto(aBs(restantePago))}
                  </span>
                )}
              </strong>
            </div>
          </div>

          {pagosBorrador.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--agx-text)' }}>
                Pagos combinados ({pagosBorrador.length})
              </div>
              {pagosBorrador.map((l, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 10px',
                    borderRadius: '10px',
                    border: '1px solid var(--agx-border)',
                    background: 'var(--agx-surface)',
                  }}
                >
                  {l.previewUrl ? (
                    <img
                      src={l.previewUrl}
                      alt="Comprobante"
                      style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '8px',
                        objectFit: 'cover',
                        border: '1px solid var(--agx-border)',
                        flexShrink: 0,
                      }}
                    />
                  ) : l.archivo ? (
                    <div
                      style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '8px',
                        background: 'var(--agx-skeleton)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        fontWeight: 700,
                        color: 'var(--agx-muted)',
                        flexShrink: 0,
                      }}
                    >
                      PDF
                    </div>
                  ) : null}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--agx-text)' }}>
                      {l.metodo_nombre}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--agx-muted)' }}>
                      ${fmtMonto(l.monto)}
                      {tasaBs && (
                        <span> ≈ {simboloBs}{fmtMonto(aBs(parseFloat(l.monto || 0)))}</span>
                      )}
                      {l.referencia ? ` · Ref: ${l.referencia}` : ''}
                    </div>
                  </div>
                  <button
                    onClick={() => quitarLineaPago(i)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#ea4335',
                      padding: '4px',
                      display: 'flex',
                    }}
                  >
                    <FiX size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              padding: '12px',
              borderRadius: '10px',
              border: '1px dashed var(--agx-border)',
            }}
          >
            <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--agx-text)' }}>
              Agregar metodo de pago
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 45%', minWidth: '160px' }}>
                <select
                  value={pagoLinea.metodo_pago_id}
                  onChange={(e) => setPagoLinea({ ...pagoLinea, metodo_pago_id: e.target.value })}
                  style={pdCampo}
                >
                  <option value="">Selecciona metodo</option>
                  {metodosPago.map((m) => (
                    <option key={m.id} value={String(m.id)}>
                      {m.nombre}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ flex: '1 1 30%', minWidth: '120px', display: 'flex', gap: '6px' }}>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={pagoLinea.monto}
                  onChange={(e) => setPagoLinea({ ...pagoLinea, monto: e.target.value })}
                  placeholder={`$${restantePago > 0 ? restantePago.toFixed(2) : totalAPagar.toFixed(2)}`}
                  style={pdCampo}
                />
                <button
                  type="button"
                  onClick={() => setPagoLinea((f) => ({ ...f, monto: String((restantePago > 0 ? restantePago : totalAPagar).toFixed(2)) }))}
                  title="Pagar todo"
                  style={{
                    padding: '0 12px',
                    borderRadius: '8px',
                    border: '1px solid var(--agx-pri2)',
                    background: 'transparent',
                    color: 'var(--agx-pri2)',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Pagar todo
                </button>
              </div>
            </div>
            {pagoLinea.monto && tasaBs && (
              <div style={{ fontSize: '12px', color: 'var(--agx-muted)' }}>
                ≈ {simboloBs}{fmtMonto(aBs(parseFloat(pagoLinea.monto || 0)))}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label
                style={{
                  flex: 1,
                  textAlign: 'center',
                  padding: '9px',
                  borderRadius: '8px',
                  border: '1px solid var(--agx-border)',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  color: 'var(--agx-pri2)',
                }}
              >
                Adjuntar comprobante
                <input
                  type="file"
                  accept="image/*,.pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => seleccionarArchivoPago(e.target.files[0] || null)}
                />
              </label>
              <button
                type="button"
                onClick={pegarImagenPago}
                style={{
                  padding: '9px 12px',
                  borderRadius: '8px',
                  border: '1px solid var(--agx-border)',
                  background: 'transparent',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  color: 'var(--agx-pri2)',
                }}
              >
                Pegar imagen
              </button>
              {pagoLinea.previewUrl && (
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <img
                    src={pagoLinea.previewUrl}
                    alt="Preview"
                    style={{
                      width: '52px',
                      height: '52px',
                      borderRadius: '8px',
                      objectFit: 'cover',
                      border: '1px solid var(--agx-border)',
                      display: 'block',
                    }}
                  />
                  <button
                    onClick={() => seleccionarArchivoPago(null)}
                    style={{
                      position: 'absolute',
                      top: '-6px',
                      right: '-6px',
                      background: '#ea4335',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '50%',
                      width: '18px',
                      height: '18px',
                      fontSize: '10px',
                      lineHeight: 1,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    ×
                  </button>
                </div>
              )}
              {pagoLinea.archivo && !pagoLinea.previewUrl && (
                <span style={{ fontSize: '11px', color: 'var(--agx-muted)' }}>{pagoLinea.archivo.name}</span>
              )}
            </div>
            <button
              onClick={agregarLineaPago}
              style={{
                padding: '9px',
                borderRadius: '8px',
                border: '1px solid var(--agx-pri2)',
                background: 'transparent',
                color: 'var(--agx-pri2)',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              + Agregar pago
            </button>
          </div>

          <button
            onClick={cargarPagoManual}
            disabled={cargandoPago || pagosBorrador.length === 0}
            style={{
              padding: '11px',
              borderRadius: '8px',
              border: 'none',
              background: 'var(--agx-pri2)',
              color: '#fff',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              opacity: cargandoPago || pagosBorrador.length === 0 ? 0.6 : 1,
            }}
          >
            {cargandoPago
              ? 'Cargando...'
              : pagosBorrador.length > 0
                ? `Guardar ${pagosBorrador.length} pago${pagosBorrador.length > 1 ? 's' : ''}`
                : 'Guardar pago'}
          </button>
        </div>
      </Modal>

      <Modal
        open={confirmarPedido}
        onClose={() => setConfirmarPedido(false)}
        width="sm"
        title="Confirmar revision"
      >
        {tieneModificaciones ? (
          <div style={{ fontSize: '13px', color: 'var(--agx-muted)' }}>
            <p style={{ margin: '0 0 12px' }}>
              El pedido tiene modificaciones. Al confirmar, se enviara un resumen al cliente para que
              apruebe los cambios.
            </p>
            <p style={{ margin: '0 0 12px', fontSize: '12px', color: '#ea4335' }}>
              <strong>Pedido #{pedido.id}</strong> - {productos.length} productos
            </p>
          </div>
        ) : (
          <p style={{ fontSize: '13px', color: 'var(--agx-muted)', margin: '0 0 12px' }}>
            El pedido no tiene modificaciones. Se aprobara directamente.
          </p>
        )}
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => setConfirmarPedido(false)}
            style={{
              flex: 1,
              padding: '10px',
              borderRadius: '10px',
              border: '1px solid var(--agx-border)',
              backgroundColor: 'var(--agx-surface)',
              color: 'var(--agx-muted)',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Volver
          </button>
          <button
            onClick={confirmarRevision}
            disabled={actualizando}
            style={{
              flex: 1,
              padding: '10px',
              borderRadius: '10px',
              border: 'none',
              backgroundColor: '#34a853',
              color: '#fff',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
          >
            <FiCheckCircle size={14} /> Confirmar
          </button>
        </div>
      </Modal>

      <Modal
        open={confirmarDevolver}
        onClose={() => setConfirmarDevolver(false)}
        width="sm"
        title={null}
      >
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              backgroundColor: isDark() ? 'rgba(234,67,53,0.15)' : '#fef2f2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 14px',
            }}
          >
            <FiRotateCcw size={28} color="#ea4335" />
          </div>
          <h3 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 700 }}>
            Devolver pedido a revision
          </h3>
          <p style={{ fontSize: '13px', color: 'var(--agx-muted)', margin: '0 0 16px', lineHeight: 1.5 }}>
            El pedido <strong>#{pedido.id}</strong> esta a credito y ya salio de revision.
            Al devolverlo, volvera a <strong>revision</strong> para que el asesor revise
            productos y precios, y se liberara el stock reservado.
          </p>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => setConfirmarDevolver(false)}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '10px',
                border: '1px solid var(--agx-border)',
                backgroundColor: 'var(--agx-surface)',
                color: 'var(--agx-muted)',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              onClick={() => {
                setConfirmarDevolver(false);
                setConfirmarDevolverFinal(true);
              }}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '10px',
                border: 'none',
                backgroundColor: '#ea4335',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Continuar
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={confirmarDevolverFinal}
        onClose={() => setConfirmarDevolverFinal(false)}
        width="sm"
        title={null}
      >
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <FiAlertTriangle size={40} color="#ea4335" style={{ marginBottom: '12px' }} />
          <h3 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 700 }}>
            Confirmar devolucion
          </h3>
          <p style={{ fontSize: '13px', color: 'var(--agx-muted)', margin: '0 0 16px', lineHeight: 1.5 }}>
            Esta accion es definitiva dentro del flujo. El pedido{' '}
            <strong>#{pedido.id}</strong> volvera a{' '}
            <strong>En Revision</strong>. El cliente sera notificado.
          </p>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => setConfirmarDevolverFinal(false)}
              disabled={actualizando}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '10px',
                border: '1px solid var(--agx-border)',
                backgroundColor: 'var(--agx-surface)',
                color: 'var(--agx-muted)',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              No, volver
            </button>
            <button
              onClick={devolverARevision}
              disabled={actualizando}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '10px',
                border: 'none',
                backgroundColor: '#ea4335',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
              }}
            >
              <FiRotateCcw size={14} /> Si, devolver
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={confirmarCancelar}
        onClose={() => setConfirmarCancelar(false)}
        width="sm"
        title={null}
      >
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <FiXCircle size={40} color="#ea4335" style={{ marginBottom: '8px' }} />
          <h3 style={{ margin: '0 0 4px', fontSize: '16px', fontWeight: 700 }}>
            Cancelar pedido #{pedido.id}
          </h3>
          <p style={{ fontSize: '12px', color: 'var(--agx-muted)', margin: 0 }}>
            El cliente sera notificado y no podra continuar con el.
          </p>
        </div>
        <textarea
          placeholder="Motivo de la cancelacion (obligatorio)..."
          value={motivoCancelacion}
          onChange={(e) => setMotivoCancelacion(e.target.value)}
          rows={3}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: '10px',
            border: '1px solid var(--agx-border)',
            fontSize: '13px',
            outline: 'none',
            fontFamily: 'inherit',
            resize: 'vertical',
            boxSizing: 'border-box',
          }}
          autoFocus
        />
        <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
          <button
            onClick={() => setConfirmarCancelar(false)}
            disabled={actualizando}
            style={{
              flex: 1,
              padding: '10px',
              borderRadius: '10px',
              border: '1px solid var(--agx-border)',
              backgroundColor: 'var(--agx-surface)',
              color: 'var(--agx-muted)',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            No, volver
          </button>
          <button
            onClick={async () => {
              if (!motivoCancelacion.trim()) return;
              setConfirmarCancelar(false);
              setActualizando(true);
              try {
                const res = await API.put(
                  `/pedidos/${pedido.id}/cancelar?schema_name=${getSchemaName()}&motivo=${encodeURIComponent(motivoCancelacion.trim())}`
                );
                if (res.data.status === 'success') setEstado('cancelado');
                else alert(res.data.message || 'Error al cancelar');
              } catch (e) {
                console.error('Error:', e);
              }
              setActualizando(false);
              setMotivoCancelacion('');
            }}
            disabled={!motivoCancelacion.trim() || actualizando}
            style={{
              flex: 1,
              padding: '10px',
              borderRadius: '10px',
              border: 'none',
              backgroundColor: '#ea4335',
              color: '#fff',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              opacity: motivoCancelacion.trim() ? 1 : 0.5,
            }}
          >
            Si, cancelar pedido
          </button>
        </div>
      </Modal>

      <Modal
        open={imagenExpandida}
        onClose={() => setImagenExpandida(false)}
        width="lg"
        title={null}
      >
        <img
          src={getImagenUrl(pedido.comprobante_url)}
          alt="Comprobante"
          style={{
            width: '100%',
            maxHeight: '75vh',
            objectFit: 'contain',
            display: 'block',
          }}
        />
      </Modal>

      <Modal
        open={confirmarSalioLocal}
        onClose={() => setConfirmarSalioLocal(false)}
        width="sm"
        title={null}
      >
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <FiTruck size={40} color="#00897b" style={{ marginBottom: '12px' }} />
          <h3 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 700 }}>
            Confirmar salida del local
          </h3>
          <p style={{ fontSize: '13px', color: 'var(--agx-muted)', margin: '0 0 20px' }}>
            Estas por marcar que el pedido #{pedido.id} salio del local por encomienda.
            Esta accion no se puede deshacer facilmente. Deseas continuar?
          </p>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => setConfirmarSalioLocal(false)}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '10px',
                border: '1px solid var(--agx-border)',
                backgroundColor: 'var(--agx-surface)',
                color: 'var(--agx-muted)',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              onClick={async () => {
                setConfirmarSalioLocal(false);
                setActualizando(true);
                try {
                  await API.put(`/pedidos/${pedido.id}/marcar-despachado`, {
                    schema_name: getSchemaName(),
                  });
                  setEstado('despachado');
                } catch (e) {
                  console.error('Error:', e);
                } finally {
                  setActualizando(false);
                }
              }}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '10px',
                border: 'none',
                backgroundColor: '#00897b',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Si, salio del local
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={confirmarEntregadoEncomienda}
        onClose={() => setConfirmarEntregadoEncomienda(false)}
        width="sm"
        title={null}
      >
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <FiCheckCircle size={40} color="#34a853" style={{ marginBottom: '12px' }} />
          <h3 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 700 }}>
            Marcar como entregado
          </h3>
          <p style={{ fontSize: '13px', color: 'var(--agx-muted)', margin: '0 0 16px' }}>
            Confirmar que el pedido #{pedido.id} fue entregado al cliente por encomienda.
          </p>
          <div style={{ marginBottom: '16px', textAlign: 'left' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--agx-text)', display: 'block', marginBottom: '6px' }}>
              Fecha de entrega
            </label>
            <input
              type="date"
              value={fechaEntregaEncomienda}
              onChange={(e) => setFechaEntregaEncomienda(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: '10px',
                border: '1px solid var(--agx-border)',
                fontSize: '14px',
                outline: 'none',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => setConfirmarEntregadoEncomienda(false)}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '10px',
                border: '1px solid var(--agx-border)',
                backgroundColor: 'var(--agx-surface)',
                color: 'var(--agx-muted)',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              onClick={async () => {
                setConfirmarEntregadoEncomienda(false);
                setActualizando(true);
                try {
                  const res = await API.put(`/pedidos/${pedido.id}/marcar-entregado-encomienda`, {
                    schema_name: getSchemaName(),
                    fecha_entrega: fechaEntregaEncomienda,
                  });
                  if (res.data.status === 'success') {
                    setEstado('entregado');
                  } else {
                    alert(res.data.message || 'Error al marcar como entregado');
                  }
                } catch (e) {
                  console.error('Error:', e);
                }
                setActualizando(false);
              }}
              disabled={actualizando}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '10px',
                border: 'none',
                backgroundColor: '#34a853',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Confirmar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const S = {
  wrapper: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'var(--agx-bg)',
  },
  wrapperEmbedded: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'transparent',
  },
  chatHeader: {
    padding: '12px 16px',
    backgroundColor: 'var(--agx-surface)',
    borderBottom: '1px solid var(--agx-border)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexShrink: 0,
  },
  header: {
    padding: '12px 20px',
    backgroundColor: 'var(--agx-surface)',
    borderBottom: '1px solid var(--agx-border)',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexShrink: 0,
  },
  backBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '6px',
    display: 'flex',
    color: 'var(--agx-muted)',
  },
  badge: {
    color: '#fff',
    padding: '5px 14px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  body: { flex: 1, overflowY: 'auto', padding: '14px' },
  sectionTitle: { fontSize: '13px', color: 'var(--agx-text)', margin: '0 0 8px' },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px 0',
    fontWeight: 600,
    fontSize: '14px',
    borderTop: '2px solid var(--agx-border)',
    marginTop: '10px',
  },
  addProductBtn: {
    padding: '8px 14px',
    backgroundColor: 'var(--agx-pri2)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  actions: {
    padding: '8px 16px',
    backgroundColor: 'var(--agx-surface)',
    borderTop: '1px solid var(--agx-border)',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    flexShrink: 0,
    alignItems: 'center',
  },
  actionBtn: {
    padding: '6px 14px',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    whiteSpace: 'nowrap',
    lineHeight: '1.4',
  },
  btn: {
    padding: '10px',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  },
};

export default PedidoDetalle;
